import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Transaction, SystemProgram, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { orderAPI, priceAPI, supportAPI, balanceAPI, bannerAPI, apiDeveloperAPI } from '../services/api';
import { QRCodeSVG } from 'qrcode.react';

const SOLANA_RECIPIENT = "5BUPvDvkxP1g9pUpJef8Vys4YWprXs8cGHVuXqraxmor";

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const [activeTab, setActiveTab] = useState('create');
  const [reactions, setReactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [tickets, setTickets] = useState([]);
  const [solPrice, setSolPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Balance states
  const [balance, setBalance] = useState(0);
  const [balanceTransactions, setBalanceTransactions] = useState([]);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [currentTopup, setCurrentTopup] = useState(null);
  const [balanceTxPage, setBalanceTxPage] = useState(1);
  const [balanceTxPerPage] = useState(10);
  const [showTxDetailModal, setShowTxDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [txSignature, setTxSignature] = useState('');
  
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  
  const [orderForm, setOrderForm] = useState({
    reaction_type: 'rocket',
    quantity: 500,
    dexscreener_link: '',
    payment_method: 'manual',
    voucher_code: ''
  });

  const [currentOrder, setCurrentOrder] = useState(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [manualTxId, setManualTxId] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [voucherMessage, setVoucherMessage] = useState({ type: '', text: '' }); // For voucher-specific messages
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', order_id: '' });
  const [ticketMessages, setTicketMessages] = useState([]);
  const [ticketReply, setTicketReply] = useState('');
  const [updatedOrderId, setUpdatedOrderId] = useState(null); // For highlighting updated orders
  const [copiedAddress, setCopiedAddress] = useState(false); // For copy feedback
  const [copiedAmount, setCopiedAmount] = useState(false); // For copy amount feedback
  
  // DexScreener validation states
  const [validatingLink, setValidatingLink] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [linkReadonly, setLinkReadonly] = useState(false);
  
  // API Developer states
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState({ name: '', whitelisted_ips: '' });
  const [apiDocs, setApiDocs] = useState(null);
  const [copiedApiKey, setCopiedApiKey] = useState(null);
  
  // Custom Pricing states
  const [userPricing, setUserPricing] = useState({
    rocket: 0.05, fire: 0.05, poop: 0.05, redflag: 0.05, favorite: 0.05
  });
  const [hasCustomPricing, setHasCustomPricing] = useState(false);



  // Helper function to truncate email/name
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to calculate payment amount with unique identifier
  const getPaymentAmount = (baseAmount, uniqueId) => {
    if (!baseAmount || !uniqueId) return '0.000000';
    
    // Fixed 6 decimals format: X.YYYYY{unique_id_2_digits}
    // Example: base 0.024315, id 88 -> 0.024388
    const baseAmountFloat = parseFloat(baseAmount);
    
    // Get first 4 decimal digits from base amount
    const baseStr = baseAmountFloat.toFixed(6);
    const parts = baseStr.split('.');
    const integerPart = parts[0] || '0';
    const decimalPart = parts[1] || '000000';
    
    // Take first 4 digits, then append unique ID (2 digits)
    const newDecimalPart = decimalPart.substring(0, 4) + uniqueId;
    
    return `${integerPart}.${newDecimalPart}`;
  };


  useEffect(() => {
    fetchReactions();
    fetchSolPrice();
    fetchOrders();
    fetchTickets();
    fetchBalance();
    fetchBanners();
    fetchUserPricing();
    if (activeTab === 'balance') {
      fetchBalanceTransactions();
    }

    // Subscribe to user-specific channel for order status updates
    const pusher = new window.Pusher('a9efe8c0829d8e2c4c91', {
      cluster: 'ap1',
      forceTLS: true
    });

    const channel = pusher.subscribe(`user-${user.id}`);
    
    // Listen for deposit approved
    channel.bind('deposit-approved', async (data) => {
      console.log('Deposit approved:', data);
      setSuccess(data.message);
      await fetchBalance();
      await fetchBalanceTransactions();
    });

    // Listen for deposit rejected
    channel.bind('deposit-rejected', async (data) => {
      console.log('Deposit rejected:', data);
      setError(data.message);
      await fetchBalanceTransactions();
    });

    channel.bind('order-status-update', (data) => {
      console.log('Order status update:', data);
      
      // Update order in the list directly for instant UI update
      if (data.order) {
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === data.order.id ? { ...order, ...data.order } : order
          )
        );
        
        // Highlight the updated order
        setUpdatedOrderId(data.order.id);
        setTimeout(() => setUpdatedOrderId(null), 3000); // Remove highlight after 3 seconds
        
        // Also update selected order if it's the one being viewed
        if (selectedOrder && selectedOrder.id === data.order.id) {
          setSelectedOrder({ ...selectedOrder, ...data.order });
        }
      } else {
        // Fallback to full refresh if no order data provided
        fetchOrders();
      }
      
      // Show notification to user
      if (data.message) {
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 5000); // Clear after 5 seconds
      }
      
      // Play audio notification for status changes
      const audio = new Audio('/notif-newmessage.wav');
      audio.play().catch(e => console.log('Audio play failed:', e));
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  }, [user.id]);

  // Pusher listener for ticket updates
  useEffect(() => {
    if (!selectedTicket) return;

    const pusher = new window.Pusher('a9efe8c0829d8e2c4c91', {
      cluster: 'ap1',
      forceTLS: true
    });

    const ticketChannel = pusher.subscribe(`ticket-${selectedTicket.id}`);
    
    ticketChannel.bind('new-message', async (data) => {
      console.log('New ticket message:', data);
      // Refresh ticket messages
      try {
        const response = await supportAPI.getTicketMessages(selectedTicket.id);
        setTicketMessages(response.data);
        
        // Play audio notification if message is from admin
        if (data.message?.sender_type === 'admin') {
          const audio = new Audio('/notif-newmessage.wav');
          audio.play().catch(e => console.log('Audio play failed:', e));
        }
      } catch (error) {
        console.error('Error fetching updated messages:', error);
      }
    });

    ticketChannel.bind('ticket-status-update', (data) => {
      console.log('Ticket status update:', data);
      // Update selected ticket status
      setSelectedTicket({ ...selectedTicket, status: data.status });
      // Refresh all tickets
      fetchTickets();
    });

    ticketChannel.bind('ticket-closed', (data) => {
      console.log('Ticket closed:', data);
      setSelectedTicket({ ...selectedTicket, status: 'closed' });
      fetchTickets();
    });

    return () => {
      ticketChannel.unbind_all();
      ticketChannel.unsubscribe();
    };
  }, [selectedTicket?.id]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown
      if (showUserMenu && !event.target.closest('.user-dropdown-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Revalidate voucher when quantity changes
  useEffect(() => {
    if (voucherInfo && orderForm.voucher_code) {
      // Automatically revalidate voucher when quantity changes
      const revalidateVoucher = async () => {
        setValidatingVoucher(true);
        setVoucherMessage({ type: '', text: '' });
        
        try {
          const totalUSD = orderForm.quantity * getCurrentPrice();
          const response = await orderAPI.validateVoucher({
            voucher_code: orderForm.voucher_code,
            total_usd: totalUSD
          });
          
          setVoucherInfo(response.data);
          setVoucherMessage({ 
            type: 'success', 
            text: `Voucher recalculated! You save $${response.data.discount_amount.toFixed(2)}` 
          });
        } catch (error) {
          setVoucherMessage({ 
            type: 'error', 
            text: error.response?.data?.detail || 'Voucher no longer valid with this quantity' 
          });
          setVoucherInfo(null);
          setOrderForm({ ...orderForm, voucher_code: '' });
        } finally {
          setValidatingVoucher(false);
        }
      };
      
      revalidateVoucher();
    }
  }, [orderForm.quantity]);

  // Auto-select balance payment if user has sufficient balance
  useEffect(() => {
    if (orderForm.quantity && orderForm.reaction_type && balance !== null) {
      const baseTotal = orderForm.quantity * getCurrentPrice();
      const finalTotal = voucherInfo 
        ? baseTotal - voucherInfo.discount_amount 
        : baseTotal;
      
      // If user has sufficient balance and payment method is not yet set to balance
      if (balance >= finalTotal && orderForm.payment_method !== 'balance') {
        console.log(`Auto-selecting balance payment: Balance $${balance} >= Order $${finalTotal}`);
        setOrderForm({ ...orderForm, payment_method: 'balance' });
      }
      // If balance is insufficient and currently set to balance, switch to manual
      else if (balance < finalTotal && orderForm.payment_method === 'balance') {
        console.log(`Insufficient balance: $${balance} < $${finalTotal}, switching to manual`);
        setOrderForm({ ...orderForm, payment_method: 'manual' });
      }
    }
  }, [orderForm.quantity, orderForm.reaction_type, balance, voucherInfo]);

  // Reset to page 1 when switching to orders tab
  useEffect(() => {
    if (activeTab === 'orders') {
      setCurrentPage(1);
    }
  }, [activeTab]);

  // Banner auto-rotation (every 5 seconds)
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchReactions = async () => {
    try {
      const response = await priceAPI.getReactions();
      setReactions(response.data);
    } catch (err) {
      console.error('Failed to fetch reactions:', err);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await balanceAPI.getBalance();
      setBalance(response.data.balance);
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    }
  };

  const fetchBalanceTransactions = async () => {
    try {
      const response = await balanceAPI.getTransactions();
      setBalanceTransactions(response.data);
    } catch (err) {
      console.error('Failed to fetch balance transactions:', err);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await apiDeveloperAPI.getApiKeys();
      setApiKeys(response.data.api_keys || []);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    }
  };

  const fetchApiDocs = async () => {
    try {
      const response = await apiDeveloperAPI.getApiDocs();
      setApiDocs(response.data);
    } catch (err) {
      console.error('Failed to fetch API docs:', err);
    }
  };

  const handleCreateApiKey = async () => {
    try {
      setLoading(true);
      const ips = apiKeyForm.whitelisted_ips
        .split(',')
        .map(ip => ip.trim())
        .filter(ip => ip.length > 0);
      
      await apiDeveloperAPI.createApiKey({
        name: apiKeyForm.name || 'Default API Key',
        whitelisted_ips: ips
      });
      
      setSuccess('API key created successfully!');
      setShowCreateApiKeyModal(false);
      setApiKeyForm({ name: '', whitelisted_ips: '' });
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    try {
      await apiDeveloperAPI.deleteApiKey(keyId);
      setSuccess('API key deleted successfully!');
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const handleRegenerateApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) return;
    try {
      await apiDeveloperAPI.regenerateApiKey(keyId);
      setSuccess('API key regenerated successfully!');
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate API key');
    }
  };

  const handleToggleApiKey = async (keyId, isActive) => {
    try {
      await apiDeveloperAPI.updateApiKey(keyId, { is_active: !isActive });
      setSuccess(`API key ${isActive ? 'disabled' : 'enabled'} successfully!`);
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update API key');
    }
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedApiKey(keyId);
    setTimeout(() => setCopiedApiKey(null), 2000);
  };

  const fetchSolPrice = async () => {
    try {
      const response = await priceAPI.getSolPrice();
      setSolPrice(response.data.price_usd);
    } catch (error) {
      console.error('Error fetching SOL price:', error);
    }
  };

  const fetchBanners = async () => {
    try {
      const response = await bannerAPI.getEnabledBanners();
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const fetchUserPricing = async () => {
    try {
      const response = await priceAPI.getUserPricing();
      if (response.data.pricing) {
        setUserPricing(response.data.pricing);
        setHasCustomPricing(response.data.has_custom_pricing);
      }
    } catch (error) {
      console.error('Error fetching user pricing:', error);
    }
  };

  // Helper function to get price for current reaction type
  const getCurrentPrice = () => {
    return userPricing[orderForm.reaction_type] || 0.05;
  };

  const handleBannerClick = async (banner) => {
    try {
      await bannerAPI.trackClick(banner.id);
      window.open(banner.link_href, '_blank');
    } catch (error) {
      console.error('Error tracking banner click:', error);
      window.open(banner.link_href, '_blank');
    }
  };

  // Validate DexScreener link
  const validateDexScreenerLink = async (link) => {
    if (!link || !link.includes('dexscreener.com')) {
      return;
    }

    setValidatingLink(true);
    setTokenInfo(null);
    setError('');
    
    try {
      console.log('Validating link:', link);
      
      // Parse URL to get chain and pairId
      // Format: https://dexscreener.com/{chain}/{pairId}
      const cleanLink = link.replace('https://dexscreener.com/', '').replace('http://dexscreener.com/', '');
      const urlParts = cleanLink.split('/').filter(part => part.length > 0);
      
      if (urlParts.length < 2) {
        setError('Invalid DexScreener link format. Expected: https://dexscreener.com/chain/address');
        setValidatingLink(false);
        return;
      }
      
      const chain = urlParts[0];
      const pairId = urlParts[1];
      
      console.log('Parsed:', { chain, pairId });
      
      // First API call: Get pair info
      const apiUrl = `https://api.dexscreener.com/latest/dex/pairs/${chain}/${pairId}`;
      console.log('Fetching from:', apiUrl);
      
      const pairResponse = await fetch(apiUrl);
      
      if (!pairResponse.ok) {
        throw new Error(`API returned status ${pairResponse.status}`);
      }
      
      const pairData = await pairResponse.json();
      console.log('Pair data received:', pairData);
      
      // Check if pairs is null or empty
      if (!pairData.pairs || pairData.pairs.length === 0) {
        // This is a token contract address, not a pair address
        console.log('Token contract detected (not a pair), showing modal');
        setContractData({ chain, pairId });
        setShowContractModal(true);
      } else {
        // Valid pair found, use the first one
        const pair = pairData.pairs[0];
        console.log('Valid pair found:', pair);
        
        setTokenInfo({
          name: pair.baseToken.name,
          symbol: pair.baseToken.symbol,
          address: pair.baseToken.address,
          url: pair.url || link
        });
        setLinkReadonly(true);
        
        console.log('Token info set successfully');
      }
    } catch (error) {
      console.error('Error validating DexScreener link:', error);
      setError(`Validation failed: ${error.message}`);
    } finally {
      setValidatingLink(false);
    }
  };

  // Handle contract to pair conversion
  const handleContractToPair = async () => {
    if (!contractData) {
      console.error('No contract data available');
      return;
    }
    
    setValidatingLink(true);
    setError('');
    
    try {
      console.log('Converting contract to pair:', contractData);
      
      // Second API call: Get token pair info from contract address
      const apiUrl = `https://api.dexscreener.com/latest/dex/tokens/${contractData.pairId}`;
      console.log('Fetching pair from token contract:', apiUrl);
      
      const tokenResponse = await fetch(apiUrl);
      
      if (!tokenResponse.ok) {
        throw new Error(`API returned status ${tokenResponse.status}`);
      }
      
      const tokenData = await tokenResponse.json();
      console.log('Token data received:', tokenData);
      
      if (tokenData.pairs && tokenData.pairs.length > 0) {
        // Find pair matching the chain, or use first pair
        let pair = tokenData.pairs.find(p => p.chainId === contractData.chain) || tokenData.pairs[0];
        
        console.log('Using pair:', pair);
        
        // Update the link with the pair URL
        const newLink = pair.url || `https://dexscreener.com/${pair.chainId}/${pair.pairAddress}`;
        
        setOrderForm({ 
          ...orderForm, 
          dexscreener_link: newLink
        });
        
        setTokenInfo({
          name: pair.baseToken.name,
          symbol: pair.baseToken.symbol,
          address: pair.baseToken.address,
          url: newLink
        });
        
        setLinkReadonly(true);
        setShowContractModal(false);
        setContractData(null);
        
        // Show success message
        setTimeout(() => {
          setSuccess('✓ Successfully switched to pair link!');
          setTimeout(() => setSuccess(''), 3000);
        }, 100);
        
        console.log('Conversion successful!');
      } else {
        console.error('No pairs found in response');
        setError('No trading pairs found for this token. Please use a valid Pair Address.');
        setShowContractModal(false);
      }
    } catch (error) {
      console.error('Error converting to pair address:', error);
      setError(`Failed to convert: ${error.message}`);
      setShowContractModal(false);
    } finally {
      setValidatingLink(false);
    }
  };

  // Handle link input change with validation
  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setOrderForm({ ...orderForm, dexscreener_link: newLink });
    
    // Reset validation states
    setTokenInfo(null);
    setLinkReadonly(false);
    
    // Trigger validation if link looks complete
    if (newLink.includes('dexscreener.com/') && newLink.split('/').length >= 5) {
      validateDexScreenerLink(newLink);
    }
  };

  // Reset link validation
  const handleResetLink = () => {
    setOrderForm({ ...orderForm, dexscreener_link: '' });
    setTokenInfo(null);
    setLinkReadonly(false);
  };

  const fetchOrders = async () => {
    try {
      const response = await orderAPI.getMyOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await supportAPI.getMyTickets();
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const handleApplyVoucher = async () => {
    if (!orderForm.voucher_code.trim()) {
      setVoucherMessage({ type: 'error', text: 'Please enter a voucher code' });
      return;
    }
    
    setValidatingVoucher(true);
    setVoucherMessage({ type: '', text: '' });
    
    try {
      const totalUSD = orderForm.quantity * getCurrentPrice();
      const response = await orderAPI.validateVoucher({
        voucher_code: orderForm.voucher_code,
        total_usd: totalUSD
      });
      
      setVoucherInfo(response.data);
      setVoucherMessage({ 
        type: 'success', 
        text: `Voucher applied! You save $${response.data.discount_amount.toFixed(2)}` 
      });
    } catch (error) {
      setVoucherMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Invalid voucher code' 
      });
      setVoucherInfo(null);
    } finally {
      setValidatingVoucher(false);
    }
  };

  const handleRemoveVoucher = () => {
    setVoucherInfo(null);
    setOrderForm({ ...orderForm, voucher_code: '' });
    setVoucherMessage({ type: '', text: '' });
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const amount = parseFloat(topupAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    setLoading(true);
    try {
      const response = await balanceAPI.createTopup({ amount_usd: amount, payment_method: 'manual' });
      setCurrentTopup(response.data);
      setSuccess('Topup request created! Please complete the payment below.');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create topup request');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract transaction ID from URL or return as-is
  const extractTxId = (input) => {
    if (!input) return '';
    
    const trimmed = input.trim();
    
    // Check if it's a Solscan URL
    const solscanMatch = trimmed.match(/solscan\.io\/tx\/([A-Za-z0-9]+)/);
    if (solscanMatch) {
      return solscanMatch[1];
    }
    
    // Check if it's a Solana Explorer URL
    const explorerMatch = trimmed.match(/explorer\.solana\.com\/tx\/([A-Za-z0-9]+)/);
    if (explorerMatch) {
      return explorerMatch[1];
    }
    
    // Otherwise, assume it's already a transaction signature
    return trimmed;
  };

  const handleVerifyTopup = async () => {
    if (!manualTxId.trim()) {
      setError('Please enter transaction signature');
      return;
    }
    
    const txId = extractTxId(manualTxId);
    
    setLoading(true);
    try {
      const response = await balanceAPI.verifyTopup({
        transaction_id: currentTopup.transaction_id,
        transaction_signature: txId
      });
      
      setSuccess(response.data.message);
      setShowTopupModal(false);
      setCurrentTopup(null);
      setTopupAmount('');
      setManualTxId('');
      
      // Refresh transactions
      await fetchBalanceTransactions();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to verify topup');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTopup = async (txId) => {
    if (!window.confirm('Are you sure you want to cancel this topup request?')) {
      return;
    }
    
    setLoading(true);
    try {
      await balanceAPI.cancelTopup(txId);
      setSuccess('Topup request cancelled');
      setShowTxDetailModal(false);
      await fetchBalanceTransactions();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to cancel topup');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate quantity before submission
    if (!validateQuantity(orderForm.quantity)) {
      setError('Quantity must be between 500-10,000 and in multiples of 100 (e.g., 500, 600, 700)');
      return;
    }
    
    // Check balance if using balance payment
    if (orderForm.payment_method === 'balance') {
      const totalCost = voucherInfo ? voucherInfo.final_total : (orderForm.quantity * getCurrentPrice());
      if (balance < totalCost) {
        setError(`Insufficient balance. You have $${balance.toFixed(2)}, need $${totalCost.toFixed(2)}`);
        return;
      }
    }
    
    setLoading(true);

    try {
      const response = await orderAPI.createOrder(orderForm);
      
      // If paid with balance, order goes straight to processing
      if (orderForm.payment_method === 'balance') {
        setSuccess('Order created and paid with balance! Processing now...');
        await fetchBalance(); // Refresh balance
        await fetchOrders(); // Refresh orders
        setOrderForm({
          reaction_type: 'rocket',
          quantity: 500,
          dexscreener_link: '',
          payment_method: 'manual',
          voucher_code: ''
        });
        setActiveTab('orders');
      } else {
        setCurrentOrder(response.data);
        setSuccess(`Order created! ${orderForm.payment_method === 'manual' ? 'Please transfer manually and submit your TxID.' : 'Please connect your wallet to proceed with payment.'}`);
        setActiveTab('payment');
      }
      
      // Reset voucher after order creation
      setVoucherInfo(null);
      setVoucherMessage({ type: '', text: '' });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoPayment = async () => {
    if (!publicKey) {
      setError('Please connect your Solana wallet');
      return;
    }

    if (!currentOrder) {
      setError('No active order found');
      return;
    }

    setError('');
    setSuccess('');
    setPaymentInProgress(true);

    try {
      const recipientPubkey = new PublicKey(currentOrder.payment_address);
      
      // Calculate payment amount with unique identifier for manual tracking
      const paymentAmount = getPaymentAmount(currentOrder.amount_sol, currentOrder.order.unique_identifier);
      const lamports = Math.floor(parseFloat(paymentAmount) * LAMPORTS_PER_SOL);

      setSuccess('Preparing transaction...');

      // Get fresh blockhash - simplified approach
      const latestBlockhash = await connection.getLatestBlockhash('confirmed');

      // Create transaction
      const transaction = new Transaction();
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: publicKey,
          toPubkey: recipientPubkey,
          lamports,
        })
      );
      
      transaction.recentBlockhash = latestBlockhash.blockhash;
      transaction.feePayer = publicKey;

      setSuccess('Please approve transaction in your wallet...');

      // Send transaction
      const signature = await sendTransaction(transaction, connection);
      
      setSuccess('Transaction sent! Waiting for confirmation...');

      // Wait for confirmation
      await connection.confirmTransaction(signature, 'confirmed');

      setSuccess('Transaction confirmed! Verifying payment...');

      // Verify payment with backend
      await orderAPI.verifyPayment({
        order_id: currentOrder.order.id,
        transaction_signature: signature,
      });

      // Redirect to success page with order data
      navigate('/order-success', {
        state: {
          orderData: {
            orderId: currentOrder.order.id,
            reactionType: currentOrder.order.reaction_type,
            quantity: currentOrder.order.quantity,
            amountSol: paymentAmount,
            transactionSignature: signature
          }
        }
      });
      
      setCurrentOrder(null);
    } catch (error) {
      console.error('Payment error:', error);
      
      // User rejected transaction
      if (error.message && error.message.includes('User rejected')) {
        setError('Transaction cancelled by user.');
      } 
      // Network or RPC error
      else if (error.message && (error.message.includes('fetch') || error.message.includes('network'))) {
        setError('Network error. Please check your internet connection and try again.');
      }
      // Generic error
      else {
        setError(error.message || 'Payment failed. Please try again or use Manual Transfer method.');
      }
    } finally {
      setPaymentInProgress(false);
    }
  };

  const handleManualPayment = async () => {
    if (!manualTxId.trim()) {
      setError('Please enter your transaction ID or URL');
      return;
    }

    if (!currentOrder) {
      setError('No active order found');
      return;
    }

    setError('');
    setSuccess('');
    setPaymentInProgress(true);

    try {
      // Extract TxID from URL if provided
      let txId = manualTxId.trim();
      
      // Check if it's a URL (contains solscan.io or solana.fm or explorer.solana.com)
      if (txId.includes('solscan.io/tx/') || txId.includes('solana.fm/tx/') || txId.includes('explorer.solana.com/tx/')) {
        // Extract the transaction signature from URL
        const parts = txId.split('/tx/');
        if (parts.length > 1) {
          txId = parts[1].split('?')[0]; // Remove query parameters if any
        }
      }

      // Submit to backend
      await orderAPI.verifyPayment({
        order_id: currentOrder.order.id,
        transaction_signature: txId,
      });

      // Redirect to success page with order data
      navigate('/order-success', {
        state: {
          orderData: {
            orderId: currentOrder.order.id,
            reactionType: currentOrder.order.reaction_type,
            quantity: currentOrder.order.quantity,
            amountSol: getPaymentAmount(currentOrder.amount_sol, currentOrder.order.unique_identifier),
            transactionSignature: txId,
            isPendingVerification: true
          }
        }
      });
      
      setCurrentOrder(null);
      setManualTxId('');
    } catch (error) {
      console.error('Manual payment error:', error);
      setError(error.response?.data?.detail || 'Verification failed. Please check your TxID.');
    } finally {
      setPaymentInProgress(false);
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailModal(true);
  };

  const handleUpdatePaymentMethod = async (orderId, newMethod) => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await orderAPI.updatePaymentMethod(orderId, {
        payment_method: newMethod
      });

      setSuccess(`Payment method updated to ${newMethod === 'auto' ? 'Wallet' : 'Manual Transfer'}`);
      
      // Update selected order with new data
      setSelectedOrder(response.data.order);
      
      // Refresh orders list
      await fetchOrders();
    } catch (error) {
      console.error('Update payment method error:', error);
      setError(error.response?.data?.detail || 'Failed to update payment method');
    } finally {
      setLoading(false);
    }
  };

  // Ticket handlers
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await supportAPI.createTicket(ticketForm);
      setSuccess('Ticket created successfully!');
      setShowCreateTicketModal(false);
      setTicketForm({ subject: '', message: '', order_id: '' });
      await fetchTickets();
    } catch (error) {
      console.error('Create ticket error:', error);
      setError(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetailModal(true);
    try {
      const response = await supportAPI.getTicketMessages(ticket.id);
      setTicketMessages(response.data);
    } catch (error) {
      console.error('Error fetching ticket messages:', error);
    }
  };

  const handleSendTicketReply = async (e) => {
    e.preventDefault();
    if (!ticketReply.trim()) return;

    try {
      await supportAPI.sendMessage(selectedTicket.id, { message: ticketReply });
      setTicketReply('');
      // Refresh messages
      const response = await supportAPI.getTicketMessages(selectedTicket.id);
      setTicketMessages(response.data);
    } catch (error) {
      console.error('Error sending reply:', error);
    }
  };

  const handleCopyAddress = async () => {
    try {
      // Determine which address to copy based on context
      const addressToCopy = currentOrder?.payment_address || SOLANA_RECIPIENT;
      await navigator.clipboard.writeText(addressToCopy);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleCopyAmount = async (amount) => {
    try {
      await navigator.clipboard.writeText(amount.toString());
      setCopiedAmount(true);
      setTimeout(() => setCopiedAmount(false), 2000);
    } catch (error) {
      console.error('Failed to copy amount:', error);
    }
  };

  const getTicketStatusBadge = (status) => {
    const statusConfig = {
      'open': { label: 'Open', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      'replied': { label: 'Replied', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      'in-progress': { label: 'In Progress', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      'resolved': { label: 'Resolved', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      'closed': { label: 'Closed', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    };
    
    const config = statusConfig[status] || statusConfig['open'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'Pending Payment', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      'pending_verification': { label: 'Pending Verification', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      'processing': { label: 'Processing', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      'completed': { label: 'Completed', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      'failed': { label: 'Payment Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      'expired': { label: 'Expired', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        {config.label}
      </span>
    );
  };


  const originalTotalUSD = orderForm.quantity * getCurrentPrice();
  const finalTotalUSD = voucherInfo ? voucherInfo.final_total : originalTotalUSD;
  const discountAmount = voucherInfo ? voucherInfo.discount_amount : 0;
  const totalSOL = solPrice ? (finalTotalUSD / solPrice).toFixed(4) : '...';

  const presetQuantities = [500, 600, 700, 800, 900, 1000, 1500, 2000];
  
  // Validate quantity is multiple of 100
  const validateQuantity = (qty) => {
    return qty >= 500 && qty <= 10000 && qty % 100 === 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Order Status Update Notification */}
      {success && (
        <div className="fixed top-20 right-4 z-[9999] animate-fadeIn max-w-md">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-4 rounded-xl shadow-2xl border border-green-400">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex items-center space-x-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-bold text-sm">Order Status Updated!</p>
                  <p className="text-xs opacity-90 mt-1">{success}</p>
                </div>
              </div>
              <button
                onClick={() => setSuccess('')}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav className="navbar px-6 lg:px-8 py-3 border-b border-purple-500/20 fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer flex-shrink-0" onClick={() => navigate('/')}>
            <img src="/logo-light.png" alt="Dexreaction" className="h-8 md:h-10 w-auto object-contain" />
          </div>
          <div className="flex items-center space-x-3 md:space-x-4 flex-shrink-0">
            {orderForm.payment_method === 'auto' && <WalletMultiButton />}
            
            {/* User Dropdown Menu */}
            <div className="relative user-dropdown-container z-[100]">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 md:px-4 py-2 rounded-lg border border-purple-500/30 hover:border-purple-500/60 transition-all bg-slate-800/70 hover:bg-slate-800"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {(user.full_name || user.email).charAt(0).toUpperCase()}
                </div>
                <span className="text-gray-200 font-medium text-sm hidden sm:inline max-w-[120px] truncate">
                  {truncateText(user.full_name || user.email, 12)}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Content */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-slate-800 border-2 border-purple-500/20 rounded-lg shadow-2xl z-[9999] overflow-hidden">
                  <div className="px-4 py-3 border-b border-purple-500/20">
                      <p className="text-sm text-gray-400">Signed in as</p>
                      <p className="text-sm font-semibold text-white truncate">{user.email}</p>
                    </div>
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/settings');
                        }}
                        className="w-full text-left px-4 py-2 text-gray-200 hover:bg-purple-500/20 transition-colors flex items-center space-x-3"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>Settings</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/support');
                        }}
                        className="w-full text-left px-4 py-2 text-gray-200 hover:bg-purple-500/20 transition-colors flex items-center space-x-3"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <span>Live Support</span>
                      </button>
                      <div className="border-t border-purple-500/20 my-2"></div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onLogout();
                        }}
                        className="w-full text-left px-4 py-2 text-red-400 hover:bg-red-500/10 transition-colors flex items-center space-x-3"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="flex min-h-[calc(100vh-64px)] pt-16">
        {/* Sidebar */}
        <aside className="w-64 bg-slate-900/50 border-r border-purple-500/20 p-4 hidden lg:block sticky top-16 h-[calc(100vh-64px)] overflow-y-auto">
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('create')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'create'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="tab-create-order"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Create Order</span>
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'payment'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="tab-payment"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <span>Payment</span>
              {currentOrder && <span className="ml-auto bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Active</span>}
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'orders'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="tab-orders"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>My Orders</span>
              {orders.length > 0 && <span className="ml-auto bg-slate-700 text-gray-300 text-xs px-2 py-0.5 rounded-full">{orders.length}</span>}
            </button>
            <button
              onClick={() => setActiveTab('tickets')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'tickets'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="tab-tickets"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
              <span>My Tickets</span>
              {tickets.length > 0 && <span className="ml-auto bg-slate-700 text-gray-300 text-xs px-2 py-0.5 rounded-full">{tickets.length}</span>}
            </button>
            <button
              onClick={() => {
                setActiveTab('balance');
                fetchBalanceTransactions();
              }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'balance'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="tab-balance"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Balance</span>
              <span className="ml-auto bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full font-semibold">${balance.toFixed(2)}</span>
            </button>
            
            {/* API Developer Menu */}
            <button
              onClick={() => {
                setActiveTab('api');
                fetchApiKeys();
                fetchApiDocs();
              }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'api'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="tab-api"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <span>API Developer</span>
            </button>
          </nav>
          
          {/* Sidebar Footer */}
          <div className="mt-8 pt-4 border-t border-purple-500/20">
            <div className="px-4 py-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">SOL Price</p>
              <p className="text-lg font-semibold text-white">${solPrice?.toFixed(2) || '...'}</p>
            </div>
          </div>
        </aside>

        {/* Mobile Menu */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-purple-500/20 z-50">
          <div className="flex justify-around py-2">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex flex-col items-center px-3 py-2 ${activeTab === 'create' ? 'text-purple-400' : 'text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-xs mt-1">Create</span>
            </button>
            <button
              onClick={() => setActiveTab('payment')}
              className={`flex flex-col items-center px-3 py-2 relative ${activeTab === 'payment' ? 'text-purple-400' : 'text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <span className="text-xs mt-1">Payment</span>
              {currentOrder && <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>}
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex flex-col items-center px-3 py-2 ${activeTab === 'orders' ? 'text-purple-400' : 'text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="text-xs mt-1">Orders</span>
            </button>
            <button
              onClick={() => setActiveTab('tickets')}
              className={`flex flex-col items-center px-3 py-2 ${activeTab === 'tickets' ? 'text-purple-400' : 'text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
              <span className="text-xs mt-1">Tickets</span>
            </button>
            <button
              onClick={() => { setActiveTab('balance'); fetchBalanceTransactions(); }}
              className={`flex flex-col items-center px-3 py-2 ${activeTab === 'balance' ? 'text-green-400' : 'text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs mt-1">${balance.toFixed(0)}</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 p-6 pb-24 lg:pb-6 overflow-auto">
        {/* Create Order Tab */}
        {activeTab === 'create' && (
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Create Order Form - Left Side (2/3 width) */}
              <div className="lg:col-span-2">
                {/* Banner Promo Display */}
                {banners.length > 0 && (
                  <div className="mb-6 relative">
                    <div className="relative overflow-hidden rounded-lg cursor-pointer group">
                      <img 
                        src={window.innerWidth >= 768 ? banners[currentBannerIndex].desktop_image_url : banners[currentBannerIndex].mobile_image_url}
                        alt={banners[currentBannerIndex].title}
                        onClick={() => handleBannerClick(banners[currentBannerIndex])}
                        className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                      />
                      {banners.length > 1 && (
                        <>
                          {/* Dots indicator */}
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {banners.map((_, index) => (
                              <button
                                key={index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentBannerIndex(index);
                                }}
                                className={`w-2 h-2 rounded-full transition-all ${index === currentBannerIndex ? 'bg-white w-6' : 'bg-white/50'}`}
                              />
                            ))}
                          </div>
                          {/* Navigation arrows */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
                            }}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
                            }}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="reaction-card">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">Create New Order</h2>
              <form onSubmit={handleCreateOrder} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Select Reaction</label>
                  <div className="grid grid-cols-5 gap-2 md:gap-4">
                    {reactions.map((reaction) => (
                      <button
                        key={reaction.id}
                        type="button"
                        onClick={() => setOrderForm({ ...orderForm, reaction_type: reaction.id })}
                        className={`p-2 md:p-4 rounded-lg border-2 transition-all ${
                          orderForm.reaction_type === reaction.id
                            ? 'border-purple-500 bg-purple-500/20'
                            : 'border-purple-500/20 hover:border-purple-500/50'
                        }`}
                        data-testid={`select-reaction-${reaction.id}`}
                      >
                        <div className="text-2xl md:text-4xl">{reaction.emoji}</div>
                        <div className="text-xs md:text-sm mt-1 capitalize font-semibold">{reaction.id}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Quantity (500 - 10,000, kelipatan 100)</label>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {presetQuantities.map((qty) => (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => setOrderForm({ ...orderForm, quantity: qty })}
                        className={`px-3 py-2 rounded-lg border transition-all text-sm ${
                          orderForm.quantity === qty
                            ? 'border-purple-500 bg-purple-500/20 text-white'
                            : 'border-purple-500/20 text-gray-300 hover:border-purple-500/50'
                        }`}
                      >
                        {qty}
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    min="500"
                    max="10000"
                    step="100"
                    className={`input-field ${!validateQuantity(orderForm.quantity) && orderForm.quantity ? 'border-red-500' : ''}`}
                    value={orderForm.quantity}
                    onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 500 })}
                    required
                    data-testid="input-quantity"
                  />
                  {!validateQuantity(orderForm.quantity) && orderForm.quantity && (
                    <p className="text-xs text-red-400 mt-1">⚠️ Quantity must be between 500-10,000 and in multiples of 100 (e.g., 500, 600, 700)</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Minimum: 500 reactions, kelipatan 100 (500, 600, 700, dst.)</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">DexScreener Link</label>
                    {linkReadonly && (
                      <button
                        type="button"
                        onClick={handleResetLink}
                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Change Link
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="url"
                      placeholder="https://dexscreener.com/solana/YOUR_PAIR_ADDRESS"
                      className={`input-field ${linkReadonly ? 'bg-slate-800/50' : ''} ${validatingLink ? 'pr-10' : ''}`}
                      value={orderForm.dexscreener_link}
                      onChange={handleLinkChange}
                      readOnly={linkReadonly}
                      required
                      data-testid="input-dexscreener-link"
                    />
                    {validatingLink && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="animate-spin h-5 w-5 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Token Info Display */}
                  {tokenInfo && (
                    <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-green-400 font-semibold text-sm mb-1">✓ Valid Pair Detected</p>
                          <p className="text-white font-medium">{tokenInfo.name} - {tokenInfo.symbol}</p>
                          <p className="text-gray-400 text-xs mt-1 break-all">{tokenInfo.address}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!tokenInfo && !validatingLink && (
                    <p className="text-gray-400 text-sm mt-1">Format: https://dexscreener.com/chain/pair</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Payment Method</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setOrderForm({ ...orderForm, payment_method: 'manual' })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        orderForm.payment_method === 'manual'
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-purple-500/20 hover:border-purple-500/50'
                      }`}
                    >
                      <div className="text-2xl mb-2">📝</div>
                      <div className="font-semibold text-sm">Manual Transfer</div>
                      <div className="text-xs text-gray-400">Transfer & submit TxID</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderForm({ ...orderForm, payment_method: 'auto' })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        orderForm.payment_method === 'auto'
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-purple-500/20 hover:border-purple-500/50'
                      }`}
                    >
                      <div className="text-2xl mb-2">🔗</div>
                      <div className="font-semibold text-sm">Connect Wallet</div>
                      <div className="text-xs text-gray-400">Automatic payment</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderForm({ ...orderForm, payment_method: 'balance' })}
                      disabled={balance <= 0}
                      className={`p-4 rounded-lg border-2 transition-all relative ${
                        orderForm.payment_method === 'balance'
                          ? 'border-green-500 bg-green-500/20'
                          : balance <= 0
                          ? 'border-gray-600 bg-gray-800/50 opacity-50 cursor-not-allowed'
                          : 'border-green-500/20 hover:border-green-500/50'
                      }`}
                    >
                      {(() => {
                        const baseTotal = orderForm.quantity * getCurrentPrice();
                        const finalTotal = voucherInfo ? baseTotal - voucherInfo.discount_amount : baseTotal;
                        const isSufficient = balance >= finalTotal && finalTotal > 0;
                        
                        return isSufficient && orderForm.payment_method === 'balance' && (
                          <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                            ✓ Auto
                          </div>
                        );
                      })()}
                      <div className="text-2xl mb-2">💰</div>
                      <div className="font-semibold text-sm">Use Balance</div>
                      <div className="text-xs text-gray-400">${balance.toFixed(2)} available</div>
                      {(() => {
                        const baseTotal = orderForm.quantity * getCurrentPrice();
                        const finalTotal = voucherInfo ? baseTotal - voucherInfo.discount_amount : baseTotal;
                        if (finalTotal > 0 && balance < finalTotal) {
                          return <div className="text-xs text-red-400 mt-1">Insufficient</div>;
                        } else if (finalTotal > 0 && balance >= finalTotal) {
                          return <div className="text-xs text-green-400 mt-1">✓ Sufficient</div>;
                        }
                        return null;
                      })()}
                    </button>
                  </div>
                  {orderForm.payment_method === 'balance' && (
                    <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm text-green-400 font-semibold mb-1">
                            Balance Payment Selected
                          </p>
                          <p className="text-xs text-gray-400">
                            {(() => {
                              const baseTotal = orderForm.quantity * getCurrentPrice();
                              const finalTotal = voucherInfo ? baseTotal - voucherInfo.discount_amount : baseTotal;
                              return `Order will be paid instantly with your balance. Current: $${balance.toFixed(2)} | Required: $${finalTotal.toFixed(2)}`;
                            })()}
                          </p>
                          <p className="text-xs text-gray-500 mt-2 italic">
                            💡 You can still choose another payment method if you prefer
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Voucher Code Section */}
                <div>
                  {/* Voucher Message - Show above input */}
                  {voucherMessage.text && (
                    <div className={`mb-3 p-3 rounded-lg border ${
                      voucherMessage.type === 'success' 
                        ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                        : 'bg-red-500/10 border-red-500/30 text-red-400'
                    }`}>
                      <div className="flex items-center">
                        {voucherMessage.type === 'success' ? (
                          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        <span className="text-sm font-medium">{voucherMessage.text}</span>
                      </div>
                    </div>
                  )}
                  
                  <label className="block text-sm font-medium mb-2">Voucher Code (Optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter voucher code"
                      className="input-field flex-1"
                      value={orderForm.voucher_code}
                      onChange={(e) => setOrderForm({ ...orderForm, voucher_code: e.target.value.toUpperCase() })}
                      disabled={!!voucherInfo}
                    />
                    {!voucherInfo ? (
                      <button
                        type="button"
                        onClick={handleApplyVoucher}
                        disabled={validatingVoucher || !orderForm.voucher_code.trim()}
                        className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed"
                      >
                        {validatingVoucher ? 'Checking...' : 'Apply'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleRemoveVoucher}
                        className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {voucherInfo && (
                    <div className="mt-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center text-green-400 text-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Voucher applied: {voucherInfo.discount_type === 'percentage' 
                          ? `${voucherInfo.discount_value}% off` 
                          : `$${voucherInfo.discount_value} off`}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300">Quantity:</span>
                    <span className="text-white font-semibold">{orderForm.quantity} reactions</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300">Price per reaction:</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">${getCurrentPrice().toFixed(3)}</span>
                      {hasCustomPricing && userPricing[orderForm.reaction_type] !== 0.05 && (
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Special Price</span>
                      )}
                    </div>
                  </div>
                  {voucherInfo && (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-300">Subtotal:</span>
                        <span className="text-white font-semibold">${originalTotalUSD.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-green-400">Discount ({voucherInfo.voucher_code}):</span>
                        <span className="text-green-400 font-semibold">-${discountAmount.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-purple-500/30 my-3"></div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg font-semibold">Total (USD):</span>
                    <span className={`text-2xl font-bold ${voucherInfo ? 'text-green-400' : 'text-purple-400'}`}>
                      ${finalTotalUSD.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Total (SOL):</span>
                    <span className={`text-2xl font-bold ${voucherInfo ? 'text-green-400' : 'text-purple-400'}`}>
                      {totalSOL} SOL
                    </span>
                  </div>
                </div>

                {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
                {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}

                {/* Warning if pair not validated yet */}
                {orderForm.dexscreener_link && !tokenInfo && !validatingLink && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start">
                    <svg className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-yellow-400 text-sm">Please wait for pair validation to complete before placing order.</p>
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed" 
                  disabled={loading || !tokenInfo || validatingLink} 
                  data-testid="btn-create-order"
                  title={!tokenInfo ? "Please enter and validate a DexScreener pair link first" : ""}
                >
                  {loading ? 'Creating Order...' : validatingLink ? 'Validating Link...' : !tokenInfo ? '⚠ Awaiting Valid Pair' : 'Create Order'}
                </button>
              </form>
                </div>
              </div>

              {/* Info Card - Right Side (1/3 width) */}
              <div className="lg:col-span-1">
                <div className="reaction-card sticky top-4">
                  {/* Header */}
                  <div className="flex items-center mb-4 pb-4 border-b border-purple-500/20">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white">Order Guide & Tips</h3>
                  </div>

                  {/* How to Fill Form */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-purple-400 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      How to Order
                    </h4>
                    <ol className="space-y-2 text-sm text-gray-300">
                      <li className="flex items-start">
                        <span className="text-purple-400 font-bold mr-2">1.</span>
                        <span><strong className="text-white">Select Reaction:</strong> Choose your desired reaction type (🚀 Rocket, 🔥 Fire, etc.)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 font-bold mr-2">2.</span>
                        <span><strong className="text-white">Set Quantity:</strong> Minimum 500, multiples of 100. More reactions = higher visibility!</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 font-bold mr-2">3.</span>
                        <span><strong className="text-white">Enter DexScreener Link:</strong> Paste the full token page URL from DexScreener</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 font-bold mr-2">4.</span>
                        <span><strong className="text-white">Apply Voucher:</strong> (Optional) Enter discount code if you have one</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 font-bold mr-2">5.</span>
                        <span><strong className="text-white">Choose Payment:</strong> Pay with SOL wallet or your balance</span>
                      </li>
                    </ol>
                  </div>

                  {/* Benefits Section */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Why Reactions Matter?
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <p className="text-green-400 font-semibold mb-1">📈 Increase Visibility</p>
                        <p className="text-gray-300">More reactions = higher ranking on DexScreener trending lists</p>
                      </div>
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <p className="text-blue-400 font-semibold mb-1">👥 Build Social Proof</p>
                        <p className="text-gray-300">Show community engagement and attract more investors</p>
                      </div>
                      <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <p className="text-purple-400 font-semibold mb-1">🎯 Stand Out</p>
                        <p className="text-gray-300">Differentiate your token from thousands of others</p>
                      </div>
                    </div>
                  </div>

                  {/* Pro Tips */}
                  <div>
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      Pro Tips
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li className="flex items-start">
                        <span className="text-yellow-400 mr-2">💡</span>
                        <span>Order during peak hours for maximum impact</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-yellow-400 mr-2">💡</span>
                        <span>Use multiple reaction types for diverse appeal</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-yellow-400 mr-2">💡</span>
                        <span>Larger quantities get better ranking boost</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-yellow-400 mr-2">💡</span>
                        <span>Check for active voucher codes to save money</span>
                      </li>
                    </ul>
                  </div>

                  {/* Quick Stats */}
                  <div className="mt-6 pt-4 border-t border-purple-500/20">
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div className="p-3 bg-purple-500/10 rounded-lg">
                        <p className="text-2xl font-bold text-purple-400">500</p>
                        <p className="text-xs text-gray-400">Min. Order</p>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg">
                        <p className="text-2xl font-bold text-green-400">Fast</p>
                        <p className="text-xs text-gray-400">Delivery</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Tab */}
        {activeTab === 'payment' && (
          <div className="max-w-2xl mx-auto">
            <div className="reaction-card">
              <h2 className="text-3xl font-bold mb-6">Complete Payment</h2>
              {currentOrder ? (
                <div className="space-y-6">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-semibold mb-4">Order Details</h3>
                    <div className="space-y-2 text-sm sm:text-base">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Reaction Type:</span>
                        <span className="text-white font-semibold capitalize flex items-center space-x-2">
                          <span className="text-2xl">
                            {currentOrder.order.reaction_type === 'rocket' && '🚀'}
                            {currentOrder.order.reaction_type === 'fire' && '🔥'}
                            {currentOrder.order.reaction_type === 'poop' && '💩'}
                            {currentOrder.order.reaction_type === 'redflag' && '🚩'}
                            {currentOrder.order.reaction_type === 'favorite' && '⭐'}
                          </span>
                          <span>{currentOrder.order.reaction_type}</span>
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Quantity:</span>
                        <div className="text-right">
                          <span className="text-white font-semibold">{currentOrder.order.quantity + Math.floor(currentOrder.order.quantity * 0.1)} reactions</span>
                          <div className="text-xs text-green-400">
                            ({currentOrder.order.quantity} + {Math.floor(currentOrder.order.quantity * 0.1)} bonus 10%)
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">DexScreener:</span>
                        <a href={currentOrder.order.dexscreener_link} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 text-xs sm:text-sm">View Token</a>
                      </div>
                      
                      {/* Voucher Discount Section */}
                      {currentOrder.order.voucher_code && currentOrder.order.discount_amount > 0 && (
                        <>
                          <div className="border-t border-purple-500/30 my-3"></div>
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-green-400 font-semibold flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                </svg>
                                Voucher Applied
                              </span>
                              <span className="text-green-400 font-bold">{currentOrder.order.voucher_code}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-300">Original Price:</span>
                              <span className="text-gray-400">${currentOrder.order.original_total_usd?.toFixed(2) || currentOrder.order.total_usd.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-300">Discount:</span>
                              <span className="text-green-400 font-semibold">-${currentOrder.order.discount_amount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm border-t border-green-500/20 pt-2">
                              <span className="text-white font-semibold">Final Price:</span>
                              <span className="text-green-400 font-bold">${currentOrder.order.total_usd.toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      )}
                      
                      <div className="border-t border-purple-500/30 my-3"></div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-300">Subtotal:</span>
                        <span className="text-white font-semibold">{currentOrder.amount_sol.toFixed(4)} SOL</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Unique Code:</span>
                        <span className="text-yellow-400 font-bold">0.00{currentOrder.order.unique_identifier}</span>
                      </div>
                      
                      <div className="border-t border-purple-500/30 my-3"></div>
                      
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-purple-500/20 p-3 rounded-lg gap-2">
                        <span className="text-base sm:text-lg font-semibold">Total Payment:</span>
                        <div className="flex items-center space-x-2 w-full sm:w-auto">
                          <div className="text-right flex-1 sm:flex-none">
                            <span className="text-xl sm:text-2xl font-bold text-purple-400 block">{getPaymentAmount(currentOrder.amount_sol, currentOrder.order.unique_identifier)} SOL</span>
                            <span className="text-xs text-gray-400">Include unique code</span>
                          </div>
                          <button
                            onClick={() => handleCopyAmount(getPaymentAmount(currentOrder.amount_sol, currentOrder.order.unique_identifier))}
                            className="flex-shrink-0 p-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 hover:border-purple-500/50 rounded-lg transition-all"
                            title="Copy amount"
                          >
                            {copiedAmount ? (
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {currentOrder.order.payment_method === 'manual' ? (
                    <div className="space-y-4">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-3">Manual Transfer Instructions</h3>
                        <ol className="list-decimal list-inside space-y-2 text-gray-300 mb-4">
                          <li>Transfer exactly <strong className="text-white">{getPaymentAmount(currentOrder.amount_sol, currentOrder.order.unique_identifier)} SOL</strong> to:</li>
                        </ol>
                        
                        {/* QR Code and Payment Address */}
                        <div className="bg-black/30 rounded-lg p-4 mb-4">
                          <div className="flex flex-col md:flex-row gap-4">
                            {/* QR Code */}
                            <div className="flex justify-center md:justify-start flex-shrink-0">
                              <div className="p-3 bg-white rounded-lg">
                                <QRCodeSVG 
                                  value={currentOrder.payment_address} 
                                  size={120}
                                  level="H"
                                  includeMargin={false}
                                />
                              </div>
                            </div>
                            
                            {/* Address with Copy Button */}
                            <div className="flex-1 flex flex-col justify-center">
                              <div className="text-sm text-gray-400 mb-2">Payment Address:</div>
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="text-white font-mono text-sm break-all flex-1">{currentOrder.payment_address}</div>
                                <button
                                  onClick={handleCopyAddress}
                                  className="flex-shrink-0 p-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 hover:border-purple-500/50 rounded-lg transition-all"
                                  title="Copy address"
                                >
                                  {copiedAddress ? (
                                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              {copiedAddress && (
                                <span className="text-green-400 text-xs">✓ Address copied!</span>
                              )}
                              <div className="text-sm text-yellow-400 font-semibold mt-2">⚠️ IMPORTANT: Transfer {getPaymentAmount(currentOrder.amount_sol, currentOrder.order.unique_identifier)} SOL (ends with .{currentOrder.order.unique_identifier} for identification)</div>
                            </div>
                          </div>
                        </div>
                        
                        <ol start="2" className="list-decimal list-inside space-y-2 text-gray-300">
                          <li>After transfer, copy your Transaction ID (TxID) or Transaction URL</li>
                          <li>Paste the TxID or URL in the field below</li>
                          <li>Click "Submit Payment" for verification</li>
                        </ol>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Transaction ID (TxID) or URL</label>
                        <input
                          type="text"
                          placeholder="Paste your transaction ID or Solscan URL here"
                          className="input-field"
                          value={manualTxId}
                          onChange={(e) => setManualTxId(e.target.value)}
                          data-testid="input-manual-txid"
                        />
                        <p className="text-xs text-gray-400 mt-1">Example: https://solscan.io/tx/... or just the TxID</p>
                      </div>

                      {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
                      {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}

                      <button
                        onClick={handleManualPayment}
                        className="btn-primary w-full"
                        disabled={paymentInProgress || !manualTxId.trim()}
                        data-testid="btn-submit-manual-payment"
                      >
                        {paymentInProgress ? 'Verifying...' : 'Submit Payment'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-3">Automatic Payment Instructions</h3>
                        <ol className="list-decimal list-inside space-y-2 text-gray-300">
                          <li>Connect your Solana wallet using the button above</li>
                          <li>Ensure you have enough SOL to cover the transaction + gas fees</li>
                          <li>Click "Send Payment" and approve the transaction in your wallet</li>
                          <li>Wait for confirmation (usually takes a few seconds)</li>
                        </ol>
                      </div>

                      <div className="flex justify-center">
                        <WalletMultiButton />
                      </div>

                      {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4">{error}</div>}
                      {success && <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4">{success}</div>}

                      <button
                        onClick={handleAutoPayment}
                        className="btn-primary w-full"
                        disabled={!publicKey || paymentInProgress}
                        data-testid="btn-send-payment"
                      >
                        {paymentInProgress ? 'Processing Payment...' : 'Send Payment'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-lg mb-4">No active order</p>
                  <button onClick={() => setActiveTab('create')} className="btn-primary">
                    Create New Order
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold">My Orders</h2>
              <button
                onClick={async () => {
                  setLoading(true);
                  await fetchOrders();
                  setLoading(false);
                }}
                disabled={loading}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh orders"
              >
                <svg 
                  className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
                <span className="hidden sm:inline">{loading ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
            {orders.length === 0 ? (
              <div className="reaction-card text-center py-12">
                <p className="text-gray-400 text-lg mb-4">No orders yet</p>
                <button onClick={() => setActiveTab('create')} className="btn-primary">
                  Create Your First Order
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {orders
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .slice((currentPage - 1) * ordersPerPage, currentPage * ordersPerPage)
                    .map((order) => (
                  <div 
                    key={order.id} 
                    className={`order-card hover:border-purple-500/50 cursor-pointer transition-all ${
                      updatedOrderId === order.id ? 'animate-pulse border-green-500 bg-green-500/10' : ''
                    }`}
                    data-testid={`order-${order.id}`}
                    onClick={() => handleOrderClick(order)}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-4xl flex-shrink-0">
                            {order.reaction_type === 'rocket' && '🚀'}
                            {order.reaction_type === 'fire' && '🔥'}
                            {order.reaction_type === 'poop' && '💩'}
                            {order.reaction_type === 'redflag' && '🚩'}
                            {order.reaction_type === 'favorite' && '⭐'}
                          </span>
                          <div>
                            <h3 className="text-xl font-semibold capitalize">{order.reaction_type}</h3>
                            <p className="text-gray-400 text-sm">{order.quantity} reactions</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-400 mb-1">${order.total_usd?.toFixed(2) || '0.00'}</div>
                        <div className="text-sm text-gray-400">{order.total_sol?.toFixed(4) || '0.0000'} SOL</div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <a 
                        href={order.dexscreener_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-purple-400 hover:text-purple-300 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View on DexScreener →
                      </a>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(order.status)}
                        {order.source && (
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            order.source === 'telegram' 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : order.source === 'api'
                              ? 'bg-purple-500/20 text-purple-400'
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {order.source === 'telegram' ? '✈️ Telegram' : order.source === 'api' ? '🔌 API' : '🌐 Web'}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-400 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {order.transaction_signature && (
                      <div className="mt-3 pt-3 border-t border-purple-500/20">
                        <a
                          href={`https://solscan.io/tx/${order.transaction_signature}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Transaction →
                        </a>
                      </div>
                    )}
                  </div>
                ))}
                </div>

                {/* Pagination */}
                {orders.length > ordersPerPage && (
                  <div className="mt-8 flex justify-center items-center space-x-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg bg-slate-800 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    {/* Page Numbers */}
                    {Array.from({ length: Math.ceil(orders.length / ordersPerPage) }, (_, i) => i + 1).map((page) => {
                      const totalPages = Math.ceil(orders.length / ordersPerPage);
                      
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 || 
                        page === totalPages || 
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-4 py-2 rounded-lg border transition-all ${
                              currentPage === page
                                ? 'bg-purple-500 border-purple-500 text-white font-bold'
                                : 'bg-slate-800 border-purple-500/20 hover:border-purple-500/50 text-gray-300'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 || 
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="text-gray-500 px-2">...</span>
                        );
                      }
                      return null;
                    })}

                    {/* Next Button */}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(orders.length / ordersPerPage)))}
                      disabled={currentPage === Math.ceil(orders.length / ordersPerPage)}
                      className="px-4 py-2 rounded-lg bg-slate-800 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    {/* Page Info */}
                    <div className="ml-4 text-sm text-gray-400">
                      Page {currentPage} of {Math.ceil(orders.length / ordersPerPage)} ({orders.length} orders)
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* My Tickets Tab */}
        {activeTab === 'tickets' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold">My Tickets</h2>
              <button
                onClick={() => {
                  setShowCreateTicketModal(true);
                  setTicketForm({ subject: '', message: '', order_id: '' });
                }}
                className="btn-primary"
              >
                + Create Ticket
              </button>
            </div>
            
            {tickets.length === 0 ? (
              <div className="reaction-card text-center py-12">
                <p className="text-gray-400 text-lg mb-4">No tickets yet</p>
                <p className="text-gray-500 text-sm">Create a ticket if you need support with your order</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div 
                    key={ticket.id} 
                    className="order-card hover:border-purple-500/50 cursor-pointer transition-all"
                    onClick={() => handleTicketClick(ticket)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-1">{ticket.subject}</h3>
                        {ticket.order_id && (
                          <p className="text-gray-400 text-sm mb-2">
                            Order: {ticket.order_id.slice(0, 8)}...
                          </p>
                        )}
                        <p className="text-gray-400 text-sm line-clamp-2">{ticket.message}</p>
                      </div>
                      {getTicketStatusBadge(ticket.status)}
                    </div>
                    <div className="flex justify-between items-center text-sm text-gray-400 pt-3 border-t border-purple-500/20">
                      <span>Ticket #{ticket.id.slice(0, 8)}...</span>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Balance Tab */}
        {activeTab === 'balance' && (
          <div className="space-y-6 max-w-6xl mx-auto">
            {/* Balance Card */}
            <div className="reaction-card">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2 flex items-center">
                    <svg className="w-8 h-8 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Your Balance
                  </h2>
                  <p className="text-4xl font-bold text-green-400">${balance.toFixed(2)}</p>
                  <p className="text-sm text-gray-400 mt-1">Available for instant payments</p>
                </div>
                <button 
                  onClick={() => setShowTopupModal(true)} 
                  className="btn-primary flex items-center whitespace-nowrap"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Top Up Balance
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm mb-1">Total Transactions</p>
                  <p className="text-2xl font-bold text-white">{balanceTransactions.length}</p>
                </div>
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm mb-1">Total Topup</p>
                  <p className="text-2xl font-bold text-green-400">
                    ${balanceTransactions.filter(t => t.type === 'topup' && t.status === 'completed').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm mb-1">Total Spent</p>
                  <p className="text-2xl font-bold text-red-400">
                    ${balanceTransactions.filter(t => t.type === 'deduct').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="reaction-card">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Transaction History
              </h3>

              {balanceTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-400">No transactions yet. Top up your balance to get started!</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-purple-500/20">
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Date</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Type</th>
                          <th className="text-left py-3 px-4 text-gray-400 font-semibold">Description</th>
                          <th className="text-right py-3 px-4 text-gray-400 font-semibold">Amount</th>
                          <th className="text-right py-3 px-4 text-gray-400 font-semibold">Balance After</th>
                          <th className="text-center py-3 px-4 text-gray-400 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {balanceTransactions
                          .slice((balanceTxPage - 1) * balanceTxPerPage, balanceTxPage * balanceTxPerPage)
                          .map((tx) => (
                            <tr 
                              key={tx.id} 
                              onClick={() => {
                                setSelectedTransaction(tx);
                                setShowTxDetailModal(true);
                              }}
                              className="border-b border-purple-500/10 hover:bg-purple-500/10 transition-colors cursor-pointer"
                            >
                              <td className="py-3 px-4 text-sm text-gray-300">
                                {new Date(tx.created_at).toLocaleDateString()}
                                <br />
                                <span className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleTimeString()}</span>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  tx.type === 'topup' ? 'bg-green-500/20 text-green-400' :
                                  tx.type === 'deduct' ? 'bg-red-500/20 text-red-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>
                                  {tx.type === 'topup' ? '⬆️ Topup' : tx.type === 'deduct' ? '⬇️ Payment' : '↩️ Refund'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-300">
                                {tx.description}
                              </td>
                              <td className={`py-3 px-4 text-right font-semibold ${
                                tx.type === 'topup' ? 'text-green-400' : 
                                tx.type === 'deduct' ? 'text-red-400' : 
                                'text-blue-400'
                              }`}>
                                {tx.type === 'topup' ? '+' : tx.type === 'deduct' ? '-' : '+'} ${tx.amount.toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-right text-white font-semibold">
                                ${tx.balance_after.toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  tx.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                  tx.status === 'verifying' ? 'bg-yellow-500/20 text-yellow-400' :
                                  tx.status === 'pending' ? 'bg-blue-500/20 text-blue-400' :
                                  tx.status === 'rejected' || tx.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {tx.status === 'completed' ? '✓ ' : 
                                   tx.status === 'verifying' ? '⏳ ' : 
                                   tx.status === 'pending' ? '⏱️ ' : 
                                   tx.status === 'rejected' || tx.status === 'cancelled' ? '✗ ' : ''}{tx.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {balanceTransactions.length > balanceTxPerPage && (
                    <div className="mt-6 flex justify-center items-center space-x-2">
                      <button
                        onClick={() => setBalanceTxPage(prev => Math.max(prev - 1, 1))}
                        disabled={balanceTxPage === 1}
                        className="px-4 py-2 rounded-lg bg-slate-800 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {Array.from({ length: Math.ceil(balanceTransactions.length / balanceTxPerPage) }, (_, i) => i + 1).map((page) => {
                        const totalPages = Math.ceil(balanceTransactions.length / balanceTxPerPage);
                        if (
                          page === 1 || 
                          page === totalPages || 
                          (page >= balanceTxPage - 1 && page <= balanceTxPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setBalanceTxPage(page)}
                              className={`px-4 py-2 rounded-lg border transition-all ${
                                balanceTxPage === page
                                  ? 'bg-purple-500 border-purple-500 text-white font-bold'
                                  : 'bg-slate-800 border-purple-500/20 hover:border-purple-500/50 text-gray-300'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === balanceTxPage - 2 || page === balanceTxPage + 2) {
                          return <span key={page} className="text-gray-500 px-2">...</span>;
                        }
                        return null;
                      })}

                      <button
                        onClick={() => setBalanceTxPage(prev => Math.min(prev + 1, Math.ceil(balanceTransactions.length / balanceTxPerPage)))}
                        disabled={balanceTxPage === Math.ceil(balanceTransactions.length / balanceTxPerPage)}
                        className="px-4 py-2 rounded-lg bg-slate-800 border border-purple-500/20 hover:border-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      <div className="ml-4 text-sm text-gray-400">
                        Page {balanceTxPage} of {Math.ceil(balanceTransactions.length / balanceTxPerPage)} ({balanceTransactions.length} transactions)
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* API Developer Tab */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">API Developer</h2>
                <p className="text-gray-400 text-sm">Create API keys and integrate with your applications</p>
              </div>
              <button
                onClick={() => setShowCreateApiKeyModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Create API Key</span>
              </button>
            </div>

            {/* API Keys List */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-lg font-semibold mb-4">Your API Keys</h3>
              
              {apiKeys.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <p>No API keys yet. Create one to get started!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {apiKeys.map((key) => (
                    <div key={key.id} className={`bg-slate-700/50 rounded-lg p-4 border ${key.is_active ? 'border-blue-500/30' : 'border-red-500/30'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold flex items-center space-x-2">
                            <span>{key.name}</span>
                            {key.is_active ? (
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Active</span>
                            ) : (
                              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Disabled</span>
                            )}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">Created: {new Date(key.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleToggleApiKey(key.id, key.is_active)}
                            className={`p-2 rounded-lg transition-colors ${key.is_active ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'}`}
                            title={key.is_active ? 'Disable' : 'Enable'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {key.is_active ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              )}
                            </svg>
                          </button>
                          <button
                            onClick={() => handleRegenerateApiKey(key.id)}
                            className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
                            title="Regenerate Key"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteApiKey(key.id)}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                            title="Delete Key"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* API Key */}
                      <div className="bg-slate-800 rounded-lg p-3 mb-3">
                        <div className="flex justify-between items-center">
                          <code className="text-sm text-blue-400 font-mono break-all">{key.api_key}</code>
                          <button
                            onClick={() => copyToClipboard(key.api_key, key.id)}
                            className="ml-2 p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors flex-shrink-0"
                          >
                            {copiedApiKey === key.id ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Whitelisted IPs */}
                      {key.whitelisted_ips && key.whitelisted_ips.length > 0 && (
                        <div className="text-xs text-gray-400">
                          <span className="font-semibold">Whitelisted IPs:</span> {key.whitelisted_ips.join(', ')}
                        </div>
                      )}
                      {(!key.whitelisted_ips || key.whitelisted_ips.length === 0) && (
                        <div className="text-xs text-yellow-400">
                          ⚠️ All IPs allowed (no whitelist)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* API Documentation */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-purple-500/20">
              <h3 className="text-lg font-semibold mb-4">API Documentation</h3>
              
              <div className="space-y-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-2">API Endpoint (POST)</p>
                  <code className="text-blue-400 bg-slate-800 px-3 py-2 rounded block">
                    {window.location.origin.replace(':3000', ':8001')}/api/v2
                  </code>
                </div>

                {/* API Info Box */}
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-4 border border-blue-500/20 mb-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Method:</span>
                      <span className="text-white ml-2 font-mono">POST</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Content-Type:</span>
                      <span className="text-white ml-2 font-mono text-xs">application/json</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Rate Limit:</span>
                      <span className="text-yellow-400 ml-2 font-semibold">20 req/min</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Payment:</span>
                      <span className="text-green-400 ml-2 font-semibold">Balance Only</span>
                    </div>
                  </div>
                </div>

                {/* Action: Services */}
                <div className="bg-slate-700/50 rounded-lg p-5 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-green-400 text-lg">Get Services List</h4>
                    <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded text-sm font-mono">action: "services"</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Retrieve all available services with pricing and limits.</p>
                  
                  {/* Parameters & Example Request side by side */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        Parameters
                      </h5>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <code className="text-blue-400 font-mono text-sm">key</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <code className="text-blue-400 font-mono text-sm">action</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="text-gray-500 text-xs">"services"</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                        Example Request
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-gray-300">{`{
  "key": "your_api_key",
  "action": "services"
}`}</pre>
                    </div>
                  </div>
                  
                  {/* Example Response full width */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                      Example Response
                    </h5>
                    <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-green-300">{`[
  {"service": 1, "name": "Rocket 🚀", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"},
  {"service": 2, "name": "Fire 🔥", "type": "reaction", "rate": "0.05", "min": "500", "max": "10000"},
  ...
]`}</pre>
                  </div>
                </div>

                {/* Action: Balance */}
                <div className="bg-slate-700/50 rounded-lg p-5 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-green-400 text-lg">Get Account Balance</h4>
                    <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded text-sm font-mono">action: "balance"</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Check your current account balance in USD.</p>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        Parameters
                      </h5>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <code className="text-blue-400 font-mono text-sm">key</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <code className="text-blue-400 font-mono text-sm">action</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="text-gray-500 text-xs">"balance"</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                        Example Request
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-gray-300">{`{
  "key": "your_api_key",
  "action": "balance"
}`}</pre>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                      <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                      Example Response
                    </h5>
                    <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-green-300">{`{"balance": "125.50000", "currency": "USD"}`}</pre>
                  </div>
                </div>

                {/* Action: Add Order */}
                <div className="bg-slate-700/50 rounded-lg p-5 mb-4 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-purple-400 text-lg">Create New Order</h4>
                    <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded text-sm font-mono">action: "add"</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Place a new order. Payment is automatically deducted from your account balance.</p>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        Parameters
                      </h5>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <code className="text-blue-400 font-mono text-sm">key</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <code className="text-blue-400 font-mono text-sm">action</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="text-gray-500 text-xs">"add"</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <div>
                            <code className="text-blue-400 font-mono text-sm">service</code>
                            <p className="text-xs text-gray-500 mt-0.5">Service ID (1-5)</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">integer</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <div>
                            <code className="text-blue-400 font-mono text-sm">link</code>
                            <p className="text-xs text-gray-500 mt-0.5">DexScreener pair URL</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <div>
                            <code className="text-blue-400 font-mono text-sm">quantity</code>
                            <p className="text-xs text-gray-500 mt-0.5">Min: 500, Max: 10000</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">integer</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                        Example Request
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-gray-300">{`{
  "key": "your_api_key",
  "action": "add",
  "service": 1,
  "link": "https://dexscreener.com/solana/abc123",
  "quantity": 1000
}`}</pre>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        Success Response
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-green-300">{`{"order": "550e8400-e29b-41d4-a716-446655440000"}`}</pre>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                        Error Response
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-red-300">{`{"error": "Insufficient balance. Required: $50.00"}`}</pre>
                    </div>
                  </div>
                </div>

                {/* Action: Status */}
                <div className="bg-slate-700/50 rounded-lg p-5 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-green-400 text-lg">Check Order Status</h4>
                    <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded text-sm font-mono">action: "status"</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Get the status of one or multiple orders.</p>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                        Parameters
                      </h5>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <code className="text-blue-400 font-mono text-sm">key</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs">required</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <code className="text-blue-400 font-mono text-sm">action</code>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="text-gray-500 text-xs">"status"</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-slate-600">
                          <div>
                            <code className="text-blue-400 font-mono text-sm">order</code>
                            <p className="text-xs text-gray-500 mt-0.5">Single order ID</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded text-xs">optional</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between py-2">
                          <div>
                            <code className="text-blue-400 font-mono text-sm">orders</code>
                            <p className="text-xs text-gray-500 mt-0.5">Comma-separated IDs (max 100)</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">string</span>
                            <span className="bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded text-xs">optional</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                        Example Request
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-gray-300">{`{
  "key": "your_api_key",
  "action": "status",
  "order": "550e8400-e29b-..."
}`}</pre>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                        Example Response
                      </h5>
                      <pre className="text-sm bg-slate-900 p-3 rounded overflow-x-auto text-green-300">{`{
  "charge": "50.00",
  "start_count": "0",
  "status": "Processing",
  "remains": "500",
  "currency": "USD"
}`}</pre>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-white mb-3 flex items-center">
                        <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                        Status Values
                      </h5>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded text-sm">Pending</span>
                        <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded text-sm">Processing</span>
                        <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded text-sm">Completed</span>
                        <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded text-sm">Cancelled</span>
                        <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded text-sm">Partial</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service IDs */}
                <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-purple-400 mb-3">Available Services</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-600 text-left">
                          <th className="py-2 text-gray-400">ID</th>
                          <th className="py-2 text-gray-400">Name</th>
                          <th className="py-2 text-gray-400">Rate</th>
                          <th className="py-2 text-gray-400">Min</th>
                          <th className="py-2 text-gray-400">Max</th>
                          <th className="py-2 text-gray-400">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-700">
                          <td className="py-2 text-blue-400 font-mono">1</td>
                          <td className="py-2">🚀 Rocket</td>
                          <td className="py-2 text-green-400">
                            ${userPricing.rocket?.toFixed(3) || '0.050'}
                            {userPricing.rocket !== 0.05 && <span className="text-xs text-purple-400 ml-1">★</span>}
                          </td>
                          <td className="py-2">500</td>
                          <td className="py-2">10,000</td>
                          <td className="py-2 text-gray-400 text-xs">Bullish momentum indicator</td>
                        </tr>
                        <tr className="border-b border-slate-700">
                          <td className="py-2 text-blue-400 font-mono">2</td>
                          <td className="py-2">🔥 Fire</td>
                          <td className="py-2 text-green-400">
                            ${userPricing.fire?.toFixed(3) || '0.050'}
                            {userPricing.fire !== 0.05 && <span className="text-xs text-purple-400 ml-1">★</span>}
                          </td>
                          <td className="py-2">500</td>
                          <td className="py-2">10,000</td>
                          <td className="py-2 text-gray-400 text-xs">Hot & trending interest</td>
                        </tr>
                        <tr className="border-b border-slate-700">
                          <td className="py-2 text-blue-400 font-mono">3</td>
                          <td className="py-2">💩 Poop</td>
                          <td className="py-2 text-green-400">
                            ${userPricing.poop?.toFixed(3) || '0.050'}
                            {userPricing.poop !== 0.05 && <span className="text-xs text-purple-400 ml-1">★</span>}
                          </td>
                          <td className="py-2">500</td>
                          <td className="py-2">10,000</td>
                          <td className="py-2 text-gray-400 text-xs">Bearish sentiment</td>
                        </tr>
                        <tr className="border-b border-slate-700">
                          <td className="py-2 text-blue-400 font-mono">4</td>
                          <td className="py-2">🚩 Flag</td>
                          <td className="py-2 text-green-400">
                            ${userPricing.redflag?.toFixed(3) || '0.050'}
                            {userPricing.redflag !== 0.05 && <span className="text-xs text-purple-400 ml-1">★</span>}
                          </td>
                          <td className="py-2">500</td>
                          <td className="py-2">10,000</td>
                          <td className="py-2 text-gray-400 text-xs">Warning/caution signal</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-blue-400 font-mono">5</td>
                          <td className="py-2">⭐ Star</td>
                          <td className="py-2 text-green-400">
                            ${userPricing.favorite?.toFixed(3) || '0.050'}
                            {userPricing.favorite !== 0.05 && <span className="text-xs text-purple-400 ml-1">★</span>}
                          </td>
                          <td className="py-2">500</td>
                          <td className="py-2">10,000</td>
                          <td className="py-2 text-gray-400 text-xs">Community favorite</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Error Codes */}
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                  <h4 className="font-semibold text-red-400 mb-3">Error Codes</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <code className="text-red-300">"Invalid API key"</code>
                      <span className="text-gray-400">API key is invalid or disabled</span>
                    </div>
                    <div className="flex justify-between">
                      <code className="text-red-300">"IP not whitelisted"</code>
                      <span className="text-gray-400">Your IP is not in the whitelist</span>
                    </div>
                    <div className="flex justify-between">
                      <code className="text-red-300">"Rate limit exceeded"</code>
                      <span className="text-gray-400">Exceeded 20 requests per minute</span>
                    </div>
                    <div className="flex justify-between">
                      <code className="text-red-300">"Insufficient balance"</code>
                      <span className="text-gray-400">Not enough balance for order</span>
                    </div>
                    <div className="flex justify-between">
                      <code className="text-red-300">"Invalid service ID"</code>
                      <span className="text-gray-400">Service ID must be 1-5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        </main>
      </div>

      {/* Create API Key Modal */}
      {showCreateApiKeyModal && (
        <div className="modal" onClick={() => setShowCreateApiKeyModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Create API Key</h2>
              <button onClick={() => setShowCreateApiKeyModal(false)} className="text-gray-400 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Key Name</label>
                <input
                  type="text"
                  value={apiKeyForm.name}
                  onChange={(e) => setApiKeyForm({...apiKeyForm, name: e.target.value})}
                  placeholder="e.g., Production Key, Test Key"
                  className="w-full bg-slate-700 border border-purple-500/30 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Whitelisted IPs (Optional)</label>
                <input
                  type="text"
                  value={apiKeyForm.whitelisted_ips}
                  onChange={(e) => setApiKeyForm({...apiKeyForm, whitelisted_ips: e.target.value})}
                  placeholder="e.g., 192.168.1.1, 10.0.0.1"
                  className="w-full bg-slate-700 border border-purple-500/30 rounded-lg px-4 py-2 focus:outline-none focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated. Leave empty to allow all IPs.</p>
              </div>
              
              <button
                onClick={handleCreateApiKey}
                disabled={loading}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                {loading ? 'Creating...' : 'Create API Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderDetailModal && selectedOrder && (
        <div className="modal" onClick={() => setShowOrderDetailModal(false)}>
          <div className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-purple-500/20">
              <div>
                <h2 className="text-2xl font-bold mb-1">Order Details</h2>
                <p className="text-gray-400 text-sm">Order ID: {selectedOrder.id.slice(0, 8)}...</p>
              </div>
              <button 
                onClick={() => setShowOrderDetailModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Order Info */}
            <div className="space-y-4">
              {/* Reaction & Status */}
              <div className="flex items-center justify-between p-5 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl">
                <div className="flex items-center space-x-4">
                  <div className="text-5xl">
                    {reactions.find(r => r.id === selectedOrder.reaction_type)?.emoji || '🚀'}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold capitalize mb-1">{selectedOrder.reaction_type}</h3>
                    <p className="text-gray-400">{selectedOrder.quantity} reactions</p>
                  </div>
                </div>
                {getStatusBadge(selectedOrder.status)}
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 bg-slate-800/50 border border-purple-500/10 rounded-xl">
                  <p className="text-gray-400 text-sm mb-2">Total Amount</p>
                  <p className="text-3xl font-bold text-purple-400">${selectedOrder.total_usd.toFixed(2)}</p>
                  <p className="text-gray-500 text-xs mt-1">USD</p>
                </div>
                <div className="p-5 bg-slate-800/50 border border-green-500/10 rounded-xl">
                  <p className="text-gray-400 text-sm mb-2">SOL Amount</p>
                  <p className="text-3xl font-bold text-green-400">{selectedOrder.total_sol.toFixed(4)}</p>
                  <p className="text-gray-500 text-xs mt-1">SOL</p>
                </div>
              </div>

              {/* Voucher Discount Information */}
              {selectedOrder.voucher_code && selectedOrder.discount_amount > 0 && (
                <div className="p-5 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl">
                  <div className="flex items-center mb-4">
                    <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h4 className="text-lg font-semibold text-green-400">Voucher Applied</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Voucher Code:</span>
                      <span className="font-bold text-green-400 text-lg">{selectedOrder.voucher_code}</span>
                    </div>
                    {selectedOrder.original_total_usd && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Original Price:</span>
                        <span className="text-gray-400 line-through">${selectedOrder.original_total_usd.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Discount:</span>
                      <span className="text-green-400 font-semibold">-${selectedOrder.discount_amount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-green-500/20 pt-3 flex justify-between items-center">
                      <span className="text-white font-semibold">Final Price:</span>
                      <span className="text-green-400 font-bold text-xl">${selectedOrder.total_usd.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-green-500/20">
                    <p className="text-xs text-gray-400 flex items-center">
                      <svg className="w-4 h-4 mr-1 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      You saved <span className="text-green-400 font-semibold mx-1">${selectedOrder.discount_amount.toFixed(2)}</span> with this voucher!
                    </p>
                  </div>
                </div>
              )}

              {/* DexScreener Link */}
              <div className="p-5 bg-slate-800/50 border border-purple-500/10 rounded-xl">
                <p className="text-gray-400 text-sm mb-3 font-medium">DexScreener Link</p>
                <a 
                  href={selectedOrder.dexscreener_link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-purple-400 hover:text-purple-300 break-all text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  {selectedOrder.dexscreener_link}
                </a>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 bg-slate-800/50 border border-purple-500/10 rounded-xl">
                  <p className="text-gray-400 text-sm mb-2">Payment Method</p>
                  <p className="font-semibold text-lg capitalize">{selectedOrder.payment_method === 'auto' ? '🔗 Wallet' : '📋 Manual Transfer'}</p>
                </div>
                <div className="p-5 bg-slate-800/50 border border-purple-500/10 rounded-xl">
                  <p className="text-gray-400 text-sm mb-2">Created Date</p>
                  <p className="font-semibold text-lg">{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                  <p className="text-gray-500 text-xs mt-1">{new Date(selectedOrder.created_at).toLocaleTimeString()}</p>
                </div>
              </div>

              {/* Transaction Signature */}
              {selectedOrder.transaction_signature && (
                <div className="p-5 bg-slate-800/50 border border-blue-500/10 rounded-xl">
                  <p className="text-gray-400 text-sm mb-3 font-medium">Transaction Signature</p>
                  <a
                    href={`https://solscan.io/tx/${selectedOrder.transaction_signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 break-all text-sm flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {selectedOrder.transaction_signature}
                  </a>
                </div>
              )}
            </div>

            {/* Payment Actions - Only show for pending orders */}
            {selectedOrder.status === 'pending' && (
              <div className="mt-6 pt-6 border-t border-purple-500/20">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Payment Options
                </h3>
                
                {error && (
                  <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4 text-sm flex items-start">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4 text-sm flex items-start">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {success}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 mb-5">
                  <button
                    onClick={() => handleUpdatePaymentMethod(selectedOrder.id, 'auto')}
                    disabled={loading || selectedOrder.payment_method === 'auto'}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      selectedOrder.payment_method === 'auto'
                        ? 'border-purple-500 bg-purple-500/20 shadow-lg'
                        : 'border-purple-500/20 hover:border-purple-500/50 hover:bg-purple-500/5'
                    }`}
                  >
                    <div className="text-3xl mb-3">🔗</div>
                    <div className="font-bold text-lg mb-1">Connect Wallet</div>
                    <div className="text-xs text-gray-400">Automatic payment via wallet</div>
                  </button>

                  <button
                    onClick={() => handleUpdatePaymentMethod(selectedOrder.id, 'manual')}
                    disabled={loading || selectedOrder.payment_method === 'manual'}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      selectedOrder.payment_method === 'manual'
                        ? 'border-purple-500 bg-purple-500/20 shadow-lg'
                        : 'border-purple-500/20 hover:border-purple-500/50 hover:bg-purple-500/5'
                    }`}
                  >
                    <div className="text-3xl mb-3">📋</div>
                    <div className="font-bold text-lg mb-1">Manual Transfer</div>
                    <div className="text-xs text-gray-400">Transfer & submit TxID</div>
                  </button>
                </div>

                {selectedOrder.payment_method === 'manual' && (
                  <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-5 mb-4">
                    <p className="text-sm text-blue-400 mb-4 font-semibold flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Manual Transfer Instructions
                    </p>
                    <div className="space-y-3 text-sm">
                      {/* QR Code and Address */}
                      <div className="flex flex-col md:flex-row gap-4 p-4 bg-slate-800/50 rounded-lg">
                        {/* QR Code */}
                        <div className="flex justify-center md:justify-start flex-shrink-0">
                          <div className="p-3 bg-white rounded-lg">
                            <QRCodeSVG 
                              value={SOLANA_RECIPIENT} 
                              size={120}
                              level="H"
                              includeMargin={false}
                            />
                          </div>
                        </div>
                        
                        {/* Address with Copy Button */}
                        <div className="flex-1 flex flex-col justify-center">
                          <span className="text-gray-400 text-xs block mb-2">Recipient Address</span>
                          <div className="flex items-center space-x-2">
                            <code className="text-purple-400 break-all font-mono text-xs flex-1">{SOLANA_RECIPIENT}</code>
                            <button
                              onClick={handleCopyAddress}
                              className="flex-shrink-0 p-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 hover:border-purple-500/50 rounded-lg transition-all"
                              title="Copy address"
                            >
                              {copiedAddress ? (
                                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          </div>
                          {copiedAddress && (
                            <span className="text-green-400 text-xs mt-2">✓ Address copied!</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <span className="text-gray-400 text-xs block mb-1">Amount</span>
                          <span className="font-bold text-green-400 text-lg">{getPaymentAmount(selectedOrder.total_sol, selectedOrder.unique_identifier)} SOL</span>
                        </div>
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <span className="text-gray-400 text-xs block mb-1">Unique ID (last 2 digits)</span>
                          <span className="font-bold text-yellow-400 text-lg">.{selectedOrder.unique_identifier}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setCurrentOrder({ order: selectedOrder, payment_address: SOLANA_RECIPIENT, amount_sol: selectedOrder.total_sol });
                    setShowOrderDetailModal(false);
                    setActiveTab('payment');
                  }}
                  className="btn-primary w-full"
                >
                  Proceed to Payment
                </button>
              </div>
            )}

            {selectedOrder.status !== 'pending' && (
              <div className="mt-6 pt-6 border-t border-purple-500/20 text-center">
                {selectedOrder.status === 'completed' ? (
                  <div className="text-green-400 p-6 bg-green-500/10 border border-green-500/20 rounded-xl">
                    <svg className="w-20 h-20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-bold text-xl mb-2">Order Completed Successfully!</p>
                    <p className="text-sm text-gray-400">Your reactions have been added to DexScreener</p>
                  </div>
                ) : selectedOrder.status === 'failed' ? (
                  <div className="text-red-400 p-6 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <svg className="w-20 h-20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-bold text-xl mb-2">Payment Rejected by Admin</p>
                    {selectedOrder.reject_reason && (
                      <div className="mt-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                        <p className="text-sm font-semibold mb-1">Reason:</p>
                        <p className="text-sm text-gray-300">{selectedOrder.reject_reason}</p>
                      </div>
                    )}
                    <p className="text-sm text-gray-400 mt-3">Please create a support ticket or submit a new order</p>
                  </div>
                ) : (
                  <div className="text-blue-400 p-6 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                    <svg className="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="font-semibold text-lg">Payment received. Waiting for admin to process your order.</p>
                  </div>
                )}
              </div>
            )}

            {/* Create Ticket for this Order */}
            <div className="mt-6 pt-6 border-t border-purple-500/20">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOrderDetailModal(false);
                  setTicketForm({
                    subject: `Issue with Order ${selectedOrder.id.slice(0, 8)}`,
                    message: '',
                    order_id: selectedOrder.id
                  });
                  setShowCreateTicketModal(true);
                }}
                className="w-full py-4 px-5 bg-gradient-to-r from-purple-500/20 to-blue-500/20 hover:from-purple-500/30 hover:to-blue-500/30 border border-purple-500/30 hover:border-purple-500/50 rounded-xl transition-all text-center group"
              >
                <span className="text-purple-400 group-hover:text-purple-300 font-bold text-lg flex items-center justify-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  Need Help? Create Support Ticket
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateTicketModal && (
        <div className="modal" onClick={() => setShowCreateTicketModal(false)}>
          <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-purple-500/20">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold">Create Support Ticket</h2>
              </div>
              <button 
                onClick={() => setShowCreateTicketModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-300">Subject *</label>
                <input
                  type="text"
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                  className="input-field"
                  placeholder="Brief description of your issue"
                  required
                  minLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-300">Message *</label>
                <textarea
                  value={ticketForm.message}
                  onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                  className="input-field resize-none"
                  placeholder="Provide detailed information about your issue"
                  rows={6}
                  required
                  minLength={5}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-300">Related Order (Optional)</label>
                <select
                  value={ticketForm.order_id}
                  onChange={(e) => setTicketForm({ ...ticketForm, order_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">-- Select an order --</option>
                  {orders.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.reaction_type.toUpperCase()} - {order.quantity} reactions - ${order.total_usd.toFixed(2)} ({order.status})
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm flex items-start">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )}

              {success && (
                <div className="text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-sm flex items-start">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {success}
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1 py-3"
                >
                  {loading ? 'Creating...' : 'Create Ticket'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateTicketModal(false)}
                  className="btn-secondary py-3 px-6"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showTicketDetailModal && selectedTicket && (
        <div className="modal" onClick={() => setShowTicketDetailModal(false)}>
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-start mb-6 pb-4 border-b border-purple-500/20">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <svg className="w-6 h-6 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <h2 className="text-2xl font-bold">{selectedTicket.subject}</h2>
                </div>
                <p className="text-gray-400 text-sm">Ticket ID: {selectedTicket.id.slice(0, 8)}...</p>
              </div>
              <div className="flex items-center space-x-3">
                {getTicketStatusBadge(selectedTicket.status)}
                <button 
                  onClick={() => setShowTicketDetailModal(false)}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Ticket Info */}
            <div className="mb-6 p-5 bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">
                    <span className="font-semibold">Created:</span> {new Date(selectedTicket.created_at).toLocaleString()}
                  </p>
                  {selectedTicket.order_id && (
                    <p className="text-gray-400 text-sm">
                      <span className="font-semibold">Related Order:</span> {selectedTicket.order_id.slice(0, 8)}...
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Thread */}
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto pr-2">
              {/* Initial Message */}
              <div className="p-5 bg-slate-800/50 border border-purple-500/10 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center mr-2">
                      <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <span className="font-bold text-purple-400">You</span>
                  </div>
                  <span className="text-gray-400 text-xs">{new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>
                <p className="text-gray-300 leading-relaxed">{selectedTicket.message}</p>
              </div>

              {/* Replies */}
              {ticketMessages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`p-5 rounded-xl ${
                    msg.sender_type === 'admin' 
                      ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30' 
                      : 'bg-slate-800/50 border border-purple-500/10'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 ${msg.sender_type === 'admin' ? 'bg-blue-500/20' : 'bg-purple-500/20'} rounded-full flex items-center justify-center mr-2`}>
                        <svg className={`w-4 h-4 ${msg.sender_type === 'admin' ? 'text-blue-400' : 'text-purple-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {msg.sender_type === 'admin' ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          )}
                        </svg>
                      </div>
                      <span className={`font-bold ${msg.sender_type === 'admin' ? 'text-blue-400' : 'text-purple-400'}`}>
                        {msg.sender_type === 'admin' ? '🛠️ Support Team' : '👤 You'}
                      </span>
                    </div>
                    <span className="text-gray-400 text-xs">
                      {new Date(msg.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(msg.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed">{msg.message}</p>
                </div>
              ))}
            </div>

            {/* Reply Form - Only show if ticket is not closed */}
            {selectedTicket.status !== 'closed' && (
              <form onSubmit={handleSendTicketReply} className="border-t border-purple-500/20 pt-6">
                <label className="block text-sm font-semibold mb-3 text-gray-300 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Reply to Ticket
                </label>
                <textarea
                  value={ticketReply}
                  onChange={(e) => setTicketReply(e.target.value)}
                  className="input-field mb-4 resize-none"
                  placeholder="Type your message..."
                  rows={4}
                  required
                />
                <button type="submit" className="btn-primary py-3 px-6">
                  <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Reply
                </button>
              </form>
            )}

            {selectedTicket.status === 'closed' && (
              <div className="text-center py-6 border-t border-purple-500/20 bg-slate-800/30 rounded-xl">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-gray-400 font-semibold">This ticket has been closed.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {showTxDetailModal && selectedTransaction && (
        <div className="modal" onClick={() => {
          setShowTxDetailModal(false);
          setTxSignature('');
        }}>
          <div className="modal-content max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-purple-500/20 flex-shrink-0">
              <h2 className="text-2xl font-bold text-white">Transaction Details</h2>
              <button 
                onClick={() => {
                  setShowTxDetailModal(false);
                  setTxSignature('');
                }}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-2" style={{maxHeight: 'calc(90vh - 120px)'}}>
              
              {/* Payment Instructions for Pending Status - MOVED TO TOP */}
              {selectedTransaction.status === 'pending' && selectedTransaction.type === 'topup' && (
                <>
                  {/* Instructions */}
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-blue-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-blue-400 font-semibold mb-2">Complete Your Payment</p>
                        <p className="text-gray-300 text-sm">
                          Transfer exactly <span className="font-bold text-green-400">${selectedTransaction.amount.toFixed(2)}</span> worth of SOL to the address below, then submit your transaction signature for verification.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Exact Amount to Send with Unique Code */}
                  {selectedTransaction.amount_sol && selectedTransaction.unique_identifier && (
                    <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-start">
                        <svg className="w-6 h-6 text-green-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-green-400 font-semibold mb-2">⚠️ IMPORTANT: Send Exact Amount</p>
                          <div className="bg-slate-900 p-3 rounded-lg mb-2">
                            <div className="text-center">
                              <p className="text-gray-400 text-xs mb-1">Transfer exactly:</p>
                              <div className="flex items-center justify-center gap-2">
                                <p className="text-3xl font-bold text-green-400 font-mono">
                                  {selectedTransaction.amount_sol.toFixed(5)} SOL
                                </p>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(selectedTransaction.amount_sol.toFixed(5));
                                    alert('Amount copied! ' + selectedTransaction.amount_sol.toFixed(5) + ' SOL');
                                  }}
                                  className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                                  title="Copy exact amount"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-2 mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                            <span className="text-yellow-400 text-sm">🔢 Your Unique Code:</span>
                            <span className="text-2xl font-bold text-yellow-400 font-mono">{selectedTransaction.unique_identifier}</span>
                          </div>
                          <p className="text-gray-400 text-xs mt-2 text-center">
                            The last 2 digits (<span className="text-yellow-400 font-bold">{selectedTransaction.unique_identifier}</span>) help us identify your payment. Do not modify the amount!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Address */}
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <label className="block text-sm font-semibold mb-3 text-gray-300">Payment Address</label>
                    <div className="flex flex-col md:flex-row items-center gap-4">
                      {/* QR Code */}
                      <div className="bg-white p-3 rounded-lg flex-shrink-0">
                        <QRCodeSVG 
                          value={SOLANA_RECIPIENT}
                          size={120}
                          level="H"
                        />
                      </div>
                      
                      {/* Address */}
                      <div className="flex-1 w-full">
                        <div className="bg-slate-900 p-3 rounded-lg mb-2">
                          <code className="text-xs text-purple-400 break-all block">
                            {SOLANA_RECIPIENT}
                          </code>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(SOLANA_RECIPIENT);
                            alert('Wallet address copied!');
                          }}
                          className="w-full px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-semibold transition-colors"
                        >
                          📋 Copy Address
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Submit Transaction Signature */}
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <label className="block text-sm font-semibold mb-2 text-gray-300">Transaction Signature / TxID *</label>
                    <input
                      type="text"
                      value={txSignature}
                      onChange={(e) => setTxSignature(e.target.value)}
                      placeholder="Paste transaction signature or Solscan URL here..."
                      className="w-full px-4 py-3 bg-slate-900 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400 font-mono text-sm"
                    />
                    <p className="text-gray-400 text-xs mt-2">
                      After transferring SOL, paste the transaction signature or Solscan URL here and click Submit Payment Proof.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={async () => {
                        if (!txSignature.trim()) {
                          alert('Please enter your transaction signature');
                          return;
                        }
                        const txId = extractTxId(txSignature);
                        setLoading(true);
                        try {
                          await balanceAPI.verifyTopup({
                            transaction_id: selectedTransaction.id,
                            transaction_signature: txId
                          });
                          alert('Payment submitted for verification! Admin will review your payment shortly.');
                          setTxSignature('');
                          setShowTxDetailModal(false);
                          fetchBalanceTransactions();
                        } catch (error) {
                          alert(error.response?.data?.detail || 'Failed to submit payment proof');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !txSignature.trim()}
                      className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed"
                    >
                      {loading ? 'Submitting...' : '✓ Submit Payment Proof'}
                    </button>
                    <button
                      onClick={() => handleCancelTopup(selectedTransaction.id)}
                      disabled={loading}
                      className="px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-purple-500/30 my-4"></div>
                </>
              )}

              {/* Transaction Details - NOW BELOW PAYMENT INSTRUCTIONS */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Transaction Details</h3>
                
                {/* Status Badge */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Status</span>
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                    selectedTransaction.status === 'completed' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                    selectedTransaction.status === 'verifying' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                    selectedTransaction.status === 'pending' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                    selectedTransaction.status === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                    'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                  }`}>
                    {selectedTransaction.status === 'completed' && '✓ '}
                    {selectedTransaction.status === 'verifying' && '⏳ '}
                    {selectedTransaction.status === 'pending' && '⏱️ '}
                    {selectedTransaction.status === 'rejected' && '✗ '}
                    {selectedTransaction.status.toUpperCase()}
                  </span>
                </div>

                {/* Transaction Info */}
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Type</span>
                    <span className={`font-semibold ${
                      selectedTransaction.type === 'topup' ? 'text-green-400' :
                      selectedTransaction.type === 'deduct' ? 'text-red-400' :
                      'text-blue-400'
                    }`}>
                      {selectedTransaction.type === 'topup' ? '⬆️ Topup' :
                       selectedTransaction.type === 'deduct' ? '⬇️ Payment' :
                       '↩️ Refund'}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount (USD)</span>
                    <span className={`text-xl font-bold ${
                      selectedTransaction.type === 'topup' ? 'text-green-400' :
                      selectedTransaction.type === 'deduct' ? 'text-red-400' :
                      'text-blue-400'
                    }`}>
                      {selectedTransaction.type === 'topup' ? '+' : selectedTransaction.type === 'deduct' ? '-' : '+'} ${selectedTransaction.amount.toFixed(2)}
                    </span>
                  </div>

                  {selectedTransaction.amount_sol && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Amount (SOL)</span>
                      <span className="text-white font-semibold font-mono">
                        {selectedTransaction.amount_sol.toFixed(5)} SOL
                      </span>
                    </div>
                  )}

                  {selectedTransaction.unique_identifier && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Unique Code</span>
                      <span className="text-yellow-400 font-bold font-mono text-lg">
                        {selectedTransaction.unique_identifier}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-gray-400">Description</span>
                    <span className="text-white text-right">{selectedTransaction.description}</span>
                  </div>

                  <div className="border-t border-purple-500/20 pt-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Balance Before</span>
                      <span className="text-white font-semibold">${selectedTransaction.balance_before.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-gray-400">Balance After</span>
                      <span className="text-white font-semibold">${selectedTransaction.balance_after.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Transaction Signature */}
                {selectedTransaction.transaction_signature && (
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-gray-400 text-sm mb-2">Transaction Signature</p>
                    <code className="text-xs bg-slate-900 p-3 rounded text-purple-400 break-all block">
                      {selectedTransaction.transaction_signature}
                    </code>
                  </div>
                )}

                {/* Rejection Reason */}
                {selectedTransaction.status === 'rejected' && selectedTransaction.rejection_reason && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 font-semibold mb-2">Rejection Reason</p>
                    <p className="text-gray-300">{selectedTransaction.rejection_reason}</p>
                  </div>
                )}

                {/* Dates */}
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Created</span>
                    <span className="text-gray-300">{new Date(selectedTransaction.created_at).toLocaleString()}</span>
                  </div>
                  {selectedTransaction.submitted_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Submitted</span>
                      <span className="text-gray-300">{new Date(selectedTransaction.submitted_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedTransaction.verified_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Verified</span>
                      <span className="text-gray-300">{new Date(selectedTransaction.verified_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Topup Balance Modal */}
      {showTopupModal && (
        <div className="modal" onClick={() => {
          if (!loading) {
            setShowTopupModal(false);
            fetchBalanceTransactions();
          }
        }}>
          <div className="modal-content max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-purple-500/20 flex-shrink-0">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white">Top Up Balance</h2>
              </div>
              <button 
                onClick={() => {
                  setShowTopupModal(false);
                  setCurrentTopup(null);
                  setTopupAmount('');
                  setManualTxId('');
                  fetchBalanceTransactions();
                }}
                disabled={loading}
                className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto pr-2" style={{maxHeight: 'calc(90vh - 120px)'}}>
            {!currentTopup ? (
              <form onSubmit={handleTopup} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Top Up Amount (USD)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    className="input-field"
                    placeholder="Enter amount (e.g., 100.00)"
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-2">Minimum: $1.00</p>
                </div>

                {topupAmount && parseFloat(topupAmount) > 0 && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300">Amount to Pay:</span>
                      <span className="text-2xl font-bold text-green-400">${parseFloat(topupAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Your Balance After:</span>
                      <span className="text-xl font-semibold text-white">${(balance + parseFloat(topupAmount)).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <button type="submit" className="btn-primary w-full" disabled={loading || !topupAmount}>
                  {loading ? 'Creating...' : 'Create Topup Request'}
                </button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 font-semibold mb-2">✓ Topup Request Created</p>
                  <p className="text-sm text-gray-300">Please transfer the exact amount below to complete your topup.</p>
                </div>

                {/* Exact Amount to Send with Unique Code */}
                {currentTopup.unique_identifier && (
                  <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-6 h-6 text-green-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-green-400 font-semibold mb-2">⚠️ IMPORTANT: Send Exact Amount</p>
                        <div className="bg-slate-900 p-3 rounded-lg mb-2">
                          <div className="text-center">
                            <p className="text-gray-400 text-xs mb-1">Transfer exactly:</p>
                            <div className="flex items-center justify-center gap-2">
                              <p className="text-3xl font-bold text-green-400 font-mono">
                                {currentTopup.amount_sol.toFixed(5)} SOL
                              </p>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(currentTopup.amount_sol.toFixed(5));
                                  setSuccess('Amount copied! ' + currentTopup.amount_sol.toFixed(5) + ' SOL');
                                }}
                                className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                                title="Copy exact amount"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            </div>
                            <p className="text-sm text-gray-400 mt-1">(≈ ${currentTopup.amount_usd.toFixed(2)} USD)</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                          <span className="text-yellow-400 text-sm">🔢 Your Unique Code:</span>
                          <span className="text-2xl font-bold text-yellow-400 font-mono">{currentTopup.unique_identifier}</span>
                        </div>
                        <p className="text-gray-400 text-xs mt-2 text-center">
                          The last 2 digits (<span className="text-yellow-400 font-bold">{currentTopup.unique_identifier}</span>) help us identify your payment. Do not modify the amount!
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Address with QR Code */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <label className="block text-sm font-semibold mb-3 text-gray-300">Payment Address</label>
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    {/* QR Code */}
                    <div className="bg-white p-3 rounded-lg flex-shrink-0">
                      <QRCodeSVG 
                        value={currentTopup.payment_address}
                        size={120}
                        level="H"
                      />
                    </div>
                    
                    {/* Address */}
                    <div className="flex-1 w-full">
                      <div className="bg-slate-900 p-3 rounded-lg mb-2">
                        <code className="text-xs text-purple-400 break-all block">
                          {currentTopup.payment_address}
                        </code>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(currentTopup.payment_address);
                          setSuccess('Wallet address copied!');
                        }}
                        className="w-full px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-semibold transition-colors"
                      >
                        📋 Copy Address
                      </button>
                    </div>
                  </div>
                </div>

                {/* Submit Transaction Signature */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <label className="block text-sm font-semibold mb-2 text-gray-300">Transaction Signature / TxID *</label>
                  <input
                    type="text"
                    value={manualTxId}
                    onChange={(e) => setManualTxId(e.target.value)}
                    className="input-field"
                    placeholder="Paste transaction signature or Solscan URL here..."
                    disabled={loading}
                    />
                  <p className="text-gray-400 text-xs mt-2">
                    You can paste the transaction signature directly or the full Solscan URL. We'll extract the signature automatically.
                  </p>
                  </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setCurrentTopup(null);
                      setManualTxId('');
                      fetchBalanceTransactions();
                    }}
                    disabled={loading}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifyTopup}
                    disabled={loading || !manualTxId.trim()}
                    className="flex-1 btn-primary"
                  >
                    {loading ? 'Verifying...' : 'Verify Payment'}
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Contract Address Modal */}
      {showContractModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" style={{ margin: 0 }}>
          <div className="bg-slate-900 rounded-lg p-6 max-w-lg w-full border border-yellow-500/30">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">Invalid Link Format</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  The link you entered appears to be a <span className="text-yellow-400 font-semibold">Token Contract Address</span>, not a <span className="text-green-400 font-semibold">Pair Address</span>.
                </p>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-slate-700">
              <p className="text-gray-300 text-sm mb-3">
                <strong className="text-white">Our service requires a Pair Address.</strong> Would you like us to automatically find and switch to the correct trading pair?
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-400">We'll fetch the correct pair URL for you</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-400">Your order will process normally</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleContractToPair}
                disabled={validatingLink}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-600 disabled:to-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed flex items-center justify-center"
              >
                {validatingLink ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Converting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Yes, Switch to Pair
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowContractModal(false);
                  setOrderForm({ ...orderForm, dexscreener_link: '' });
                }}
                disabled={validatingLink}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-white rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
